// ============================================================================
// IMPORTS SECTION
// ============================================================================
// React core hooks and utilities
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
// Form management
import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
// UI Components
import Select from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { components } from "react-select";
// Routing
import { useNavigate, useLocation } from "react-router-dom";
// Toast notifications
import { showSuccess, showError } from "toast/toast";
// Error handling
import { handleApiError } from "utils/errorHandler";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
// Google Maps integration
import { useLoadScript, Autocomplete } from "@react-google-maps/api";
// Address management
import AddressFormModal from "components/AddAddressFormModal";
// Payment processing
import { loadStripe } from "@stripe/stripe-js";
// Custom UI components
import { UIReactSelectPro, UIInputPro } from "components/OrdersSelectInput";
// Image handling
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
// Assets
import imagecustomRounded from "assets/images/imagecustomrounded.png";
import { AppIcon } from "components/AppIcon";
// Type definitions
import {
  Product,
  Agent,
  Office,
  License,

  Order,
  ShippingCost,
  Addresses,
  TaxRule,
  TaxGroup,
} from "types";
// Product selection modal
import ProductPickerModal, {
  type StudioBatchVariantPreviewMap,
  type StudioCustomizationFlowProps,
} from "components/ordercheckoutpop";
import { downloadWorkDriveFile, getCustomizationBatch } from "api/realesyStudioApi";
import {
  contactFromOrderSnapshot,
  effectiveLicenseTypeForItem,
  findBuyerSearchMatchForStudioRestore,
  isMongoObjectId,
  normalizeCustomizationBatchContexts,
  studioBatchVariantPreviewsFromBatchItems,
  studioBatchVariantPreviewsFromCartLines,
  studioRestoreAddressBindings,
  studioRestoreChargesSeedsFromOrder,
} from "utils/signageStudioBatchRestore";
// Tax and shipping components
import ShippingOptions, { type ShippingOptionsV2Handle } from "components/Tax/ShippingOptionsui";
import PONumber from "components/PONumber";
import SpecialFeePicker, { type SpecialFeePickerV2Handle } from "components/Tax/SpecialFeeui";
import ProjectGetCard from "components/projectgetcard";
import { DiscountAmountDisplay, getSignageOrderLinePricing } from "components/DiscountLabelBadge";
// Utility functions
import CustomItemModal from "components/CustomItemModal";
import SearchBrokerageAgentTeam, {
  type SearchBrokerageAgentTeamHandle,
} from "components/SearchBrokerageAgentTeam";
import CompactApprovedProofCard from "components/CompactApprovedProofCard";
import {
  fetchProjectById,
  fetchProjectApprovedMessage,
  resolveApprovedProofVersionSummary,
  fetchPublicProjectMessagesByProjectId,
} from "components/proejctsandquoteapicalls";
import { isSignagePortalRoute } from "utils/isSignagePortalRoute";
import { buildSalesOrderPayloadForApi, findSalesOrderAddressById, resolveSalesOrderAddressId } from "components/SignageCreateOrder/buildSalesOrderPayloadForApi";
import {
  postSalesOrderResolve,
  buildSalesResolvePayload,
  pickSalesResolveBuyerContext,
  applySalesResolveResponse,
  mergeSelectedProductsFromResolved,
  overlaySalesSubmitFromResolved,
  type ResolvedSalesOrder,
} from "orderResolve";
import { readResolveTaxable, resolveCatalogProductLineTaxable } from "orderResolve/utils/readResolveTaxable";
import { getSalesResolveDiscountCode } from "utils/salesResolveDiscountCode";
import {
  applyUserLockedShippingAmounts,
  mergeResolvedShippingTaxIntoLocal,
  shippingOptionIdsKey,
} from "orderResolve/utils/shippingAmountSync";
import { runSignageCreateOrderPostCreateEffects } from "components/SignageCreateOrder/runSignageCreateOrderPostCreateEffects";
import type {
  OfficeAgentOption,
  BrokerageOption,
  TeamMemberOption,
  ProductObj,
  PickerImage,
  PickerPayload,
  ShipPayload,
  FeePayload,
  ShipSelectedIds,
} from "components/SignageCreateOrder/types";
import {
  SignageCreateOrderInstructionsAndTotals,
  uploadOrderDocumentsToOrder,
} from "components/SignageCreateOrder/SignageCreateOrderInstructionsAndTotals";
import { GENERAL_SETTINGS_KEYS } from "pages/signage/SignageGeneralSettings";
import "components/SearchBrokerageAgentTeam.css";
import { getAuthCookie } from "../../utils/authCookies";

/** True when id is missing or MongoDB placeholder all-zeros (same pattern as other signage flows). */
function isUnsetMongoId(id: unknown): boolean {
  const s = String(id ?? "").trim();
  return !s || s === "000000000000000000000000";
}

/** Placeholder contact phone when none is on file (matches SearchBrokerageAgentTeam). */
const DEFAULT_SIGNAGE_CONTACT_PHONE = "0000000000";

function withDefaultSignageContactPhone(v: unknown): string {
  const s = String(v ?? "").trim();
  return s || DEFAULT_SIGNAGE_CONTACT_PHONE;
}

/** Prefer API `phone1`, then `phone`, on user-like objects. */
function primaryPhoneFromUserLike(u: Record<string, unknown> | null | undefined): string {
  if (!u) return "";
  const p1 = String(u.phone1 ?? "").trim();
  if (p1) return p1;
  return String(u.phone ?? "").trim();
}

const pickImages = (
  selectedVariant: any,
  productObj: any,
  imagesFromUI?: PickerImage[]
) => {
  if (imagesFromUI?.length) return imagesFromUI;
  if (selectedVariant?.images?.length) return selectedVariant.images;
  return productObj?.product?.images ?? [];
};

// Custom dropdown indicator component
const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: '#6c757d' }}></i>
    </components.DropdownIndicator>
  );
};
const keyFor = (r: { productId: string; variantId: string }) =>
  `${r.productId}:${r.variantId}`;
const formatSkuDisplay = (sku?: string | null) => {
  const raw = String(sku ?? "").trim();
  if (!raw) return "-";
  if (/^custom_sku(?:_|$)/i.test(raw)) return "Custom SKU";
  return raw.includes("_") ? raw.split("_").slice(0, -1).join("_") || raw : raw;
};

// Custom styles to ensure dropdown indicator is always visible
const customSelectStyles = {
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    display: 'flex !important',
    opacity: 1,
    visibility: 'visible',
  }),
  indicatorSeparator: (provided: any, state: any) => ({
    ...provided,
    display: 'none',
  }),
  menu: (provided: any) => ({
    ...provided,
    zIndex: 9999,
  }),
  menuList: (provided: any) => ({
    ...provided,
    maxHeight: 240,
    padding: 0,
  }),
};

const libraries: Array<"places"> = ["places"];
const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const stripePromise = loadStripe(
  "pk_test_51QomrvRrSyvk2AKVSU1SPxfeIHewNcpjHwExcuHxWMiZKPFiwAahkPsIFWNYIEBQ4poy6mKZiyNoZAFYjtpsDv1100nHhjT0bK"
);

// Unique ID key for localStorage
const UNIQUE_ID_KEY = "draft_unique_id";

// Function to ensure draft unique ID
async function ensureDraftUniqueId(token?: string, cartId?: string | null): Promise<string | null> {
  try {
    // 1) if already cached, return it
    const existing = localStorage.getItem(UNIQUE_ID_KEY);
    if (existing) return existing;

    if (!token) return null;

    // 2) else fetch one from API and cache it
    const url = cartId
      ? `${process.env.REACT_APP_API_URL}/utils/new-unique-id?cart_id=${encodeURIComponent(cartId)}`
      : `${process.env.REACT_APP_API_URL}/utils/new-unique-id`;

    const res = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(errorData.message || `Failed to fetch unique ID: ${res.statusText}`);
    }

    const data = await res.json().catch(() => ({}));
    const id = data?.id || data?.data?.id || data?.data?.unique_id || data?.unique_id || null;
    if (!id) {
      throw new Error("Unique ID not found in API response");
    }

    localStorage.setItem(UNIQUE_ID_KEY, id);
    return id;
  } catch (error) {
    // Send error email notification and log to file
    const userId = getAuthCookie("userId");
    const url = cartId
      ? `${process.env.REACT_APP_API_URL}/utils/new-unique-id?cart_id=${encodeURIComponent(cartId)}`
      : `${process.env.REACT_APP_API_URL}/utils/new-unique-id`;

    // File name and line number are automatically extracted from error stack trace
    await handleApiError(error, {
      module: "sales_order",
      action: "ensure_draft_unique_id",
      userId: userId || undefined,
      licenseType: getAuthCookie("licenseType") || undefined,
      userName: getAuthCookie("userName") || undefined,
      url: url,
      method: "GET",
      additionalInfo: {
        cartId: cartId || null,
        hasCachedId: !!localStorage.getItem(UNIQUE_ID_KEY)
      }
    }, token);

    return null;
  }
}

// Fetch a single new unique ID (no cache) - for custom product/variant IDs
async function fetchNewUniqueId(token: string): Promise<string> {
  const url = `${process.env.REACT_APP_API_URL}/utils/new-unique-id`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(errorData.message || `Failed to fetch unique ID: ${res.statusText}`);
  }
  const data = await res.json().catch(() => ({}));
  const id = data?.id || data?.data?.id || data?.data?.unique_id || data?.unique_id || null;
  if (!id) throw new Error("Unique ID not found in API response");
  return String(id);
}




const ORDER_TYPE_BADGE_MAP: Record<string, { label: string; cls: string }> = {
  BROKERAGE_FIRM: { label: "Brokerage", cls: "search-bat-badge search-bat-badge-b" },
  OFFICE: { label: "Office", cls: "search-bat-badge search-bat-badge-o" },
  BROKERAGE_AGENT: { label: "Brokerage Agent", cls: "search-bat-badge search-bat-badge-ba" },
  AGENT: { label: "Agent", cls: "search-bat-badge search-bat-badge-a" },
  BROKERAGE_TEAM_MEMBER: { label: "Team Member", cls: "search-bat-badge search-bat-badge-m" },
  TEAM_MEMBER: { label: "Team Member", cls: "search-bat-badge search-bat-badge-m" },
};


// ============================================================================
// MAIN COMPONENT: SignageCreateOrder
// ============================================================================
//
// BUSINESS PURPOSE
//   Screen to create signage sales orders: resolve buyer (brokerage / office / agent / team member),
//   add catalog and custom line items, billing/shipping addresses, shipping option + special fees,
//   run tax/coupon engine, then submit POST /orders and navigate to order details.
//
// WHERE TO LOOK FOR BUSINESS LOGIC (refactors / debugging)
//   • Request JSON for API:  components/SignageCreateOrder/buildSalesOrderPayloadForApi.ts
//   • Post-save (project link, emails, PDF, Stripe URLs, notifications):
//                            components/SignageCreateOrder/runSignageCreateOrderPostCreateEffects.ts
//   • Buyer/ship/fee TypeScript types: components/SignageCreateOrder/types.ts
//   • Instructions + “Order Totals” card UI: components/SignageCreateOrder/SignageCreateOrderInstructionsAndTotals.tsx
//   • This file: Formik, SearchBrokerageAgentTeam, product/custom rows, tax engine hooks, Save / draft submit.
//
// KEY FLOWS (high level)
//   1) Buyer search / continue → selectedBrokerageOption, agent, office, team member → customer_id rules in payload.
//   2) Sales order resolve → productTotals, productTaxMap, liveDiscountDisplay.
//   3) Save → buildOrderPayloadForApiRef → buildSalesOrderPayloadForApi(values, deps) → POST /orders.
//   4) Success → immediate navigation + toast; emails/notifications run in background (see post-create module).
//


const SignageCreateOrder = () => {


  // ========================================================================
  // STATE MANAGEMENT - UI & Form States
  // ========================================================================
  const [preview, setPreview] = useState<string | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0); // Track upload progress

  // Centralized busy state tracking for async operations
  const [busyStates, setBusyStates] = useState({
    shipping: false,      // Shipping options are being calculated
    specialFee: false,    // Special fees are being calculated
    productTax: false,   // Product tax is being calculated
    mainForm: false,      // Main form is being submitted
  });
  const isAnythingBusy = React.useMemo(
    () => formSubmitting || Object.values(busyStates).some(b => b),
    [formSubmitting, busyStates]
  );
  const handleShippingBusyChange = React.useCallback((busy: boolean) => {
    setBusyStates(prev => ({ ...prev, shipping: busy }));
  }, []);

  const handleSpecialFeeBusyChange = React.useCallback((busy: boolean) => {
    setBusyStates(prev => ({ ...prev, specialFee: busy }));
  }, []);


  // ========================================================================
  // ROUTING & AUTHENTICATION
  // ========================================================================
  const navigate = useNavigate();
  const safeNavigate = createSafeNavigate(navigate);
  const location = useLocation();
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
  const projectIdFromQuery = useMemo(
    () => new URLSearchParams(location.search).get("project_id") || "",
    [location.search]
  );
  const [linkedProjectId, setLinkedProjectId] = useState<string | null>(() => projectIdFromQuery || null);
  const [linkedProjectNumber, setLinkedProjectNumber] = useState<string | null>(null);
  const [approvedMessageData, setApprovedMessageData] = useState<any>(null);
  const [hasApprovedMessage, setHasApprovedMessage] = useState(false);
  const userClearedProjectRef = React.useRef(false);

  useEffect(() => {
    if (projectIdFromQuery && !userClearedProjectRef.current) {
      setLinkedProjectId(projectIdFromQuery);
    }
  }, [projectIdFromQuery]);

  // Authentication tokens and user info from cookies
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const userName = getAuthCookie("userName");
  const licenseType = getAuthCookie("licenseType");

  // Batch restore: when Studio redirects back with ?batch_id=xxx&context_id=yyy,
  // fetch the batch and patch studio customization data onto matching product rows.
  const studioReturnBatchId = useMemo(
    () => new URLSearchParams(location.search).get("batch_id") || "",
    [location.search]
  );
  const studioReturnContextId = useMemo(
    () => new URLSearchParams(location.search).get("context_id") || "",
    [location.search]
  );


  // ========================================================================
  // STATE MANAGEMENT - Buyer & Organization Selection
  // ========================================================================
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const [parentSignageName, setParentSignageName] = useState<string | null>(null);
  const [addressObjects, setAddressObjects] = useState<any[]>([]);
  const [officeAgents, setOfficeAgents] = useState<Agent[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const pid = String(linkedProjectId || "").trim();
    if (!token || !pid) {
      setHasApprovedMessage(false);
      setApprovedMessageData(null);
      return;
    }
    (async () => {
      try {
        const approved = await fetchProjectApprovedMessage(token, pid);
        if (cancelled) return;
        const hasMessage = Boolean(approved?.has_approved_message);
        setHasApprovedMessage(hasMessage);
        setApprovedMessageData(hasMessage ? approved?.data ?? null : null);
      } catch {
        if (cancelled) return;
        setHasApprovedMessage(false);
        setApprovedMessageData(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linkedProjectId, token]);


  // ========================================================================
  // STATE MANAGEMENT - Products & Order
  // ========================================================================
  const [ProductObjs, setProductObjs] = useState<ProductObj[]>([]);
  const [selectedProductItem, setSelectedProductItem] = useState<ProductObj | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [shippingCosts, setShippingCosts] = useState<ShippingCost[]>([]);
  const [selectedTaxRate, setSelectedTaxRate] = useState(0);
  const [buyerType, setBuyerType] = useState<string>("");
  const [formError, setFormError] = useState<string | null>(null);


  // ========================================================================
  // STATE MANAGEMENT - Pricing & Discounts
  // ========================================================================
  // Map to store effective prices from assignment discount API
  // Key format: "productId:variantId", Value: effective_price from API
  const [effectiveMap, setEffectiveMap] = useState<Record<string, number>>({});
  // Key format: "productId:variantId", Value: base_price from resolve-assignment-discount API
  const [basePriceMap, setBasePriceMap] = useState<Record<string, number>>({});

  // Map to track manually edited unit prices (overrides effective prices)
  // Key format: "productId:variantId", Value: manually set price
  const [manualUnitPriceMap, setManualUnitPriceMap] = useState<Record<string, number>>({});

  // ========== SALES ORDER RESOLVE ==========
  const lastResolvedSalesRef = React.useRef<ResolvedSalesOrder | null>(null);
  const isRecomputingRef = React.useRef(false);
  const recomputeRequestIdRef = React.useRef(0);
  const pendingRecomputeRef = React.useRef(false);
  const [isRecomputingSales, setIsRecomputingSales] = React.useState(false);
  const [couponInput, setCouponInput] = React.useState("");
  const [couponResolveError, setCouponResolveError] = React.useState("");
  const [liveDiscountDisplay, setLiveDiscountDisplay] = React.useState({ code: "", amount: 0 });
  const [resolvedTotalsTick, setResolvedTotalsTick] = React.useState(0);
  const userClearedCouponRef = React.useRef(false);
  const recomputeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSalesRecomputeRef = React.useRef<
    ((override?: {
      discountCode?: string;
      force?: boolean;
      feePayload?: FeePayload;
      shipPayload?: ShipPayload;
    }) => void) | null
  >(null);
  const formikValuesRef = React.useRef<Record<string, unknown>>({});
  const recomputeSalesRef = React.useRef<
    ((override?: {
      discountCode?: string;
      force?: boolean;
      feePayload?: FeePayload;
      shipPayload?: ShipPayload;
    }) => Promise<boolean>) | null
  >(null);
  const [baseId, setBaseId] = useState("")


  // ========================================================================
  // STATE MANAGEMENT - Buyer Selection (AsyncPaginate Options)
  // ========================================================================
  const [selectedBrokerageOption, setSelectedBrokerageOption] = useState<BrokerageOption | null>(null);
  const [selectedTeamMemberOption, setSelectedTeamMemberOption] = useState<TeamMemberOption | null>(null);
  const [projectContactPrefill, setProjectContactPrefill] = useState<{
    label: string;
    type: string;
    contact: { fName: string; lName: string; email: string; phone: string };
  } | null>(null);
  const [customerGroupId, setCustomerGroupId] = useState<string | null>(null);
  /** Ref to hold team member from onContinue payload so order payload always has id/name even if state hasn't flushed */
  const teamMemberFromContinueRef = useRef<{ value: string; label: string } | null>(null);
  const buildOrderPayloadForApiRef = useRef<((values: any) => Promise<Record<string, unknown> | null>) | null>(null);
  const [orderTypeBadge, setOrderTypeBadge] = useState<{ label: string; cls: string } | null>(null);


  // ========================================================================
  // STATE MANAGEMENT - Tax Calculation
  // ========================================================================
  const [taxBreakdown, setTaxBreakdown] = useState<{ product: number; shipping: number; specialFee: number }>({
    product: 0,
    shipping: 0,
    specialFee: 0,
  });

  // Product totals from resolve (after discounts and tax calculation)
  const [productTotals, setProductTotals] = useState({
    subTotal: 0,     // discounted products subtotal (tax base after discounts)
    discount: 0,     // total product discount applied
    productTax: 0,   // product-only tax total
  });
  safeConsole.log("productTotals:", productTotals);

  // Tax maps: productId:variantId -> tax amount/percentage
  const [productTaxMap, setProductTaxMap] = useState<Record<string, number>>({});
  const [productTaxPctMap, setProductTaxPctMap] = React.useState<Record<string, number>>({});


  // ========================================================================
  // STATE MANAGEMENT - Shipping & Special Fees
  // ========================================================================
  // Shipping amounts (calculated by ShippingOptions component)
  const [shippingAmount, setShippingAmount] = useState(0);
  const [shippingTaxAmount, setShippingTaxAmount] = useState(0);

  // Special fee amounts (calculated by SpecialFeePicker component)
  const [specialFeeAmount, setSpecialFeeAmount] = useState(0);
  const [specialFeeTaxAmount, setSpecialFeeTaxAmount] = useState(0);

  // ========================================================================
  // STATE MANAGEMENT - Manual Edits & UI Controls
  // ========================================================================
  // Manual shipping amount editing
  const [isEditingShipping, setIsEditingShipping] = useState(false);
  const [manualShippingAmount, setManualShippingAmount] = useState<string>("");
  /** User edited shipping in Order Totals card — qty/rate recalc must not overwrite amount. */
  const userEditedShippingAmountRef = useRef(false);

  // Tax details visibility toggle
  const [showTaxDetails, setShowTaxDetails] = useState(false);

  // Order notes
  const [notes, setNotes] = React.useState<string>("");
  /** Pending instruction attachments (local files) — uploaded after POST /orders succeeds. */
  const pendingInstructionFilesRef = useRef<{ id: string; file: File }[]>([]);
  const handlePendingInstructionFilesChange = React.useCallback((items: { id: string; file: File }[]) => {
    pendingInstructionFilesRef.current = items;
  }, []);


  // ========================================================================
  // STATE MANAGEMENT - Coupon Discounts
  // ========================================================================
  // Per-item coupon discount allocation map
  // Key format: "productId:variantId", Value: discount amount for that line item
  const [perItemCouponMap, setPerItemCouponMap] = React.useState<Record<string, number>>({});
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customProductSettingEnabled, setCustomProductSettingEnabled] = useState(false);
  // Custom line items from CustomItemModal (no catalog product)
  const [customproducts, setCustomproducts] = useState<Array<{
    product_id: string;
    variant_id: string;
    item_name: string;
    quantity: number;
    sku?: string;
    Price: number;
    is_taxable?: boolean;
    item_tax_info?: { taxable?: boolean };
    custom_information: string;
    length: number;
    width: number;
    height: number;
    weight: number;
  }>>([]);
  const PRODUCT_FIELD_MIN = 1;
  const purchasedQtyMinMsg = "Quantity must be at least 1.";
  const purchasedUnitPriceMinMsg = "Unit price must be at least 1.";
  type PurchasedProductFieldErrors = { quantity?: string; unitPrice?: string };
  const [purchasedProductFieldErrors, setPurchasedProductFieldErrors] = useState<
    Record<string, PurchasedProductFieldErrors>
  >({});
  const purchasedProductErrorKey = (kind: "catalog" | "custom", id: string) => `${kind}:${id}`;
  const validatePurchasedQuantityValue = (raw: string | number | undefined): string | undefined => {
    if (raw === "" || raw === undefined || raw === null) return purchasedQtyMinMsg;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < PRODUCT_FIELD_MIN) return purchasedQtyMinMsg;
    return undefined;
  };
  const validatePurchasedUnitPriceValue = (raw: string | number | undefined): string | undefined => {
    if (raw === "" || raw === undefined || raw === null) return purchasedUnitPriceMinMsg;
    const trimmed = String(raw).trim();
    if (trimmed === "" || trimmed === ".") return purchasedUnitPriceMinMsg;
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < PRODUCT_FIELD_MIN) return purchasedUnitPriceMinMsg;
    return undefined;
  };
  const patchPurchasedProductFieldError = (
    errorKey: string,
    field: keyof PurchasedProductFieldErrors,
    message?: string
  ) => {
    setPurchasedProductFieldErrors((prev) => {
      const next = { ...prev };
      const row = { ...(next[errorKey] || {}) };
      if (message) row[field] = message;
      else delete row[field];
      if (Object.keys(row).length === 0) delete next[errorKey];
      else next[errorKey] = row;
      return next;
    });
  };
  const validateAllPurchasedProductFieldsRef = useRef<() => boolean>(() => true);


  // ========================================================================
  // REFS - Component References
  // ========================================================================
  const shipRef = React.useRef<ShippingOptionsV2Handle>(null);
  const feeRef = React.useRef<SpecialFeePickerV2Handle>(null);
  /** When true, office change must not wipe line items (Studio return restore in progress). */
  const studioRestoreGuardRef = useRef(false);
  /** When true, buyer restore from project_id must not clear project message addresses. */
  const projectRestoreGuardRef = useRef(false);
  const projectMessageAddressesRef = useRef<{
    billing: any | null;
    shipping: any | null;
    isSame: boolean;
    shippingMethodName?: string;
  } | null>(null);
  const restoredStudioReturnKeyRef = useRef("");
  const searchBatRestoreRef = useRef<SearchBrokerageAgentTeamHandle | null>(null);
  const addressObjectsMirrorRef = useRef<any[]>([]);
  const addressOwnerTokenRef = useRef(0);

  const upsertAddressInList = (list: any[], addr: any): any[] => {
    const id = resolveSalesOrderAddressId(addr) || String(addr?._id || "").trim();
    if (!id) return Array.isArray(list) ? list : [];
    const current = Array.isArray(list) ? [...list] : [];
    const idx = current.findIndex(
      (a: any) =>
        resolveSalesOrderAddressId(a) === id ||
        String(a?._id || "") === id ||
        String(a?.address_details?._id || "") === id
    );
    if (idx >= 0) return Array.isArray(list) ? list : current;
    current.push(addr);
    return current;
  };

  const mergeProjectPrefilledAddresses = (list: any[]): any[] => {
    const prefilled = projectMessageAddressesRef.current;
    if (!prefilled) return Array.isArray(list) ? list : [];
    let merged = Array.isArray(list) ? [...list] : [];
    if (prefilled.billing) merged = upsertAddressInList(merged, prefilled.billing);
    if (prefilled.shipping) merged = upsertAddressInList(merged, prefilled.shipping);
    return merged;
  };

  const applyProjectMessageAddressesToUi = () => {
    const prefilled = projectMessageAddressesRef.current;
    if (!prefilled || (!prefilled.billing && !prefilled.shipping)) return;
    const { billing: billingAddr, shipping: shippingAddr, isSame, shippingMethodName } = prefilled;
    setAddressObjects((prev) => mergeProjectPrefilledAddresses(prev));
    const billingId = resolveSalesOrderAddressId(billingAddr);
    const shippingId = resolveSalesOrderAddressId(shippingAddr);
    if (billingId) {
      formik.setFieldValue("billingAddressId", billingId, false);
      setSelectedBillingAddress(billingAddr);
      setSelectedOfficeBillingAddress(billingAddr);
    }
    if (shippingId) {
      formik.setFieldValue("shippingAddressId", shippingId, false);
      setSelectedShippingAddress(shippingAddr);
      setSelectedOfficeShippingAddress(shippingAddr);
    }
    formik.setFieldValue("isSameAsBilling", isSame, false);
    if (shippingMethodName) {
      setSelectedShippingMethod({
        value: shippingMethodName,
        label: shippingMethodName,
        cost: 0,
      });
      formik.setFieldValue("shippingMethod", shippingMethodName, false);
    }
  };
  const [addressSelectKey, setAddressSelectKey] = useState(0);


  // ========================================================================
  // STATE MANAGEMENT - Shipping & Fee Payloads
  // ========================================================================
  const [shipPayloadState, setShipPayloadState] = React.useState<ShipPayload>({
    shipping_option: [],
    isManual: false,
    isUps: false
  });
  const shipPayloadRef = React.useRef<ShipPayload>({
    shipping_option: [],
    isManual: false,
    isUps: false,
  });
  /** Studio return: feed ShippingOptions the same props as edit mode (match API-loaded options by id). */
  const [studioRestoreShippingInitialIds, setStudioRestoreShippingInitialIds] = React.useState<{
    manual_shipping_id: string | null;
    ups_service_code: string | null;
    integration_id?: string | null;
    shipper_address_id?: string | null;
  } | null>(null);
  const [studioRestoreShippingSavedRows, setStudioRestoreShippingSavedRows] = React.useState<any[] | null>(null);
  /** Studio return: pre-check special fees from batch `order_charges[].specialfee[]` once list loads. */
  const [studioRestoreSpecialFeeInitialIds, setStudioRestoreSpecialFeeInitialIds] = React.useState<string[] | null>(null);
  /** Add Product modal: show Studio preview image when batch item matches product+variant. */
  const [studioBatchVariantPreviews, setStudioBatchVariantPreviews] =
    React.useState<StudioBatchVariantPreviewMap | null>(null);
  // Key incremented on search clear to remount ShippingOptions (clear their internal state)
  const [orderFormResetKey, setOrderFormResetKey] = React.useState(0);


  // ========================================================================
  // HELPER FUNCTIONS - Fee Payload Creation
  // ========================================================================
  // Creates an empty fee payload structure
  const createEmptyFeePayload = React.useCallback(
    (): FeePayload => ({
      specialfee: [],
      selected_fee_ids: [],
      total_amount: 0,
      total_tax_amount: 0,
      total_tax_percentage: 0,
    }),
    []
  );
  const [feePayloadState, setFeePayloadState] = React.useState<FeePayload>(() => createEmptyFeePayload());
  const feePayloadRef = React.useRef<FeePayload>(createEmptyFeePayload());
  const specialFeeCatalogRef = React.useRef<unknown[]>([]);


  // ========================================================================
  // HELPER FUNCTIONS - Key Generation & Utilities
  // ========================================================================
  // Generates a unique key for a product row: "productId:variantId"
  const keyForRow = (r: { productId: string; variantId: string }) => `${r.productId}:${r.variantId}`;

  // Get selected brokerage ID
  const brokerageId = selectedBrokerageOption?.value || null;

  // Normalize ID from various possible formats (id, _id, etc.)
  const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");

  // Pagination page size for async selects
  const PAGE_SIZE = 10;


  // ========================================================================
  // ASYNC DATA LOADERS - Brokerage Firms
  // ========================================================================
  // Loads brokerage firm options for AsyncPaginate dropdown
  // Supports pagination and search functionality
  const loadBrokerageOptions: LoadOptions<BrokerageOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      try {
        const q = (inputValue || "").trim();

        // Build API URL with search and pagination parameters
        const url =
          `${process.env.REACT_APP_API_URL}/users-search` +
          `?licenseType=BROKERAGE_FIRM` +
          `&signageFirmID=${encodeURIComponent((isSignageTeammember && parentSignageId ? parentSignageId : userId) || "")}` +
          `&page=${page}&limit=${PAGE_SIZE}` +
          (q ? `&search=${encodeURIComponent(q)}` : "");


        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          safeConsole.error("Failed to fetch brokerage firms:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();

        const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

        const options: BrokerageOption[] = list.map((u: any) => ({
          value: normalizeId(u),
          label: u?.firmDetails?.companyName || u?.text || "(no name)",
          firstName: u?.firstName || "",
          lastName: u?.lastName || "",
          email: u?.email || "",
          phone: withDefaultSignageContactPhone(primaryPhoneFromUserLike(u)),
        }));

        const hasMore = Array.isArray(json)
          ? options.length === PAGE_SIZE
          : json?.pagination?.more ??
          (json?.total ? page * (json?.limit ?? PAGE_SIZE) < json.total : options.length === PAGE_SIZE);

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        safeConsole.error("Failed to load brokerage options:", err);
        // Send error email notification
        await handleApiError(err, {
          module: "sales_order",
          action: "load_brokerage_options",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/users-search`,
          method: "GET",
          additionalInfo: {
            page,
            search: inputValue || "",
            isSignageTeammember,
            parentSignageId: parentSignageId || null
          }
        }, token);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [userId, isSignageTeammember, parentSignageId, token, licenseType, userName]
  );

  // Note: Office, Team Member, and Office Agent loaders moved after state declarations


  // ========================================================================
  // ASYNC DATA LOADERS - Agents
  // ========================================================================
  // Loads agent options for AsyncPaginate dropdown
  // Uses Select2 endpoint for agent search with pagination
  const loadAgentOptions: LoadOptions<OfficeAgentOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();
      const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;

      try {
        // Guard: require signageId to proceed
        if (!signageId) {
          return { options: [], hasMore: false, additional: { page: 1 } };
        }

        const url =
          `${process.env.REACT_APP_API_URL}/users/signage/${signageId}/agents/select2` +
          `?page=${page}&limit=${PAGE_SIZE}` +
          (q ? `&search=${encodeURIComponent(q)}` : "");

        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          safeConsole.error("Failed to fetch agents:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();

        const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

        const options: OfficeAgentOption[] = list.map((u: any) => ({
          value: normalizeId(u),
          label: u?.name || `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "(no name)",
          firstName: u?.firstName || "",
          lastName: u?.lastName || "",
          email: u?.email || "",
          phone: withDefaultSignageContactPhone(primaryPhoneFromUserLike(u)),
        }));

        const hasMore = Array.isArray(json)
          ? options.length === PAGE_SIZE
          : json?.pagination?.more ??
          (json?.total ? page * (json?.limit ?? PAGE_SIZE) < json.total : options.length === PAGE_SIZE);

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        safeConsole.error("Failed to load agent options:", err);
        // Send error email notification
        await handleApiError(err, {
          module: "sales_order",
          action: "load_agent_options",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/users/signage/${signageId}/agents/select2`,
          method: "GET",
          additionalInfo: {
            page,
            search: inputValue || "",
            signageId: signageId || null,
            isSignageTeammember,
            parentSignageId: parentSignageId || null
          }
        }, token);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [userId, isSignageTeammember, parentSignageId, token, licenseType, userName]
  );

  const [selectedOfficeAgent, setSelectedOfficeAgent] = useState<{
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null>(null);

  const [selectedAgent, setSelectedAgent] = useState<{
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null>(null);

  const [selectedOffice, setSelectedOffice] = useState<{
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  } | null>(null);


  // ========================================================================
  // ASYNC DATA LOADERS - Offices
  // ========================================================================
  // Office option type for select dropdowns
  type OfficeOption = {
    value: string;
    label: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };

  // Loads office options for AsyncPaginate dropdown
  // Requires brokerageId to be selected first (NEW FLOW)
  const loadOfficeOptions: LoadOptions<OfficeOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      // Guard: require brokerage ID to proceed
      if (!brokerageId) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      try {
        const q = (inputValue || "").trim();

        const url = `${process.env.REACT_APP_API_URL}/office/select/${brokerageId}`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page,
            limit: PAGE_SIZE,
            search: q,
          }),
        });

        if (!response.ok) {
          safeConsole.error("Failed to fetch offices:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();

        const list = json?.results || [];

        const options: OfficeOption[] = list.map((office: any) => {
          const addr0 = office.officeAddress?.[0] as { phone?: string; phone1?: string } | undefined;
          return {
            value: office.id || office._id,
            label: office.officeName || "---",
            firstName: office.officeName || "",
            lastName: "",
            email: "",
            phone: withDefaultSignageContactPhone(
              String(addr0?.phone1 ?? addr0?.phone ?? "").trim()
            ),
          };
        });

        const hasMore = json?.pagination?.more ?? false;

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        safeConsole.error("Failed to load office options:", err);
        // Send error email notification
        await handleApiError(err, {
          module: "sales_order",
          action: "load_office_options",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/office/select/${brokerageId}`,
          method: "POST",
          additionalInfo: {
            page,
            search: inputValue || "",
            brokerageId: brokerageId || null
          }
        }, token);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [brokerageId, token, userId, licenseType, userName]
  );


  // ========================================================================
  // ASYNC DATA LOADERS - Team Members
  // ========================================================================
  // Loads team member options for AsyncPaginate dropdown
  // Requires office to be selected first (based on office - NEW FLOW)
  const loadTeamMemberOptions: LoadOptions<TeamMemberOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      // HARD GUARD: require office ID to proceed
      const officeId = selectedOffice?.value;
      if (!officeId) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      try {
        const q = (inputValue || "").trim();

        const url = `${process.env.REACT_APP_API_URL}/office/${officeId}/team-members/select`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page,
            limit: PAGE_SIZE,
            search: q,
          }),
        });

        if (!response.ok) {
          safeConsole.error("Failed to fetch team members:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();

        const list = json?.results || [];

        const options: TeamMemberOption[] = list.map((u: any) => ({
          value: String(u?.id ?? u?._id ?? ""),
          label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "---",
          firstName: u?.firstName || "",
          lastName: u?.lastName || "",
          email: u?.email || "",
          phone: withDefaultSignageContactPhone(primaryPhoneFromUserLike(u)),
          offices: [],
        }));

        const hasMore = json?.pagination?.more ?? false;

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        safeConsole.error("Failed to load team member options:", err);
        // Send error email notification
        await handleApiError(err, {
          module: "sales_order",
          action: "load_team_member_options",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/office/${officeId}/team-members/select`,
          method: "POST",
          additionalInfo: {
            page,
            search: inputValue || "",
            officeId: officeId || null
          }
        }, token);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [selectedOffice?.value, token, userId, licenseType, userName]
  );


  // ========================================================================
  // ASYNC DATA LOADERS - Office Agents
  // ========================================================================
  // Loads office agent options for AsyncPaginate dropdown
  // Requires office to be selected first (based on office - NEW FLOW)
  const loadOfficeAgentOptions: LoadOptions<OfficeAgentOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      // HARD GUARD: require office ID to proceed
      const officeId = selectedOffice?.value;
      if (!officeId) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      try {
        const q = (inputValue || "").trim();

        const url = `${process.env.REACT_APP_API_URL}/agent/select/${officeId}`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            page,
            limit: PAGE_SIZE,
            search: q,
          }),
        });

        if (!response.ok) {
          safeConsole.error("Failed to fetch office agents:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();

        const list = json?.results || [];

        const options: OfficeAgentOption[] = list.map((u: any) => ({
          value: String(u?.id ?? u?._id ?? ""),
          label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "---",
          firstName: u?.firstName || "",
          lastName: u?.lastName || "",
          email: u?.email || "",
          phone: withDefaultSignageContactPhone(primaryPhoneFromUserLike(u)),
        }));

        const hasMore = json?.pagination?.more ?? false;

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        safeConsole.error("Failed to load office agent options:", err);
        // Send error email notification
        await handleApiError(err, {
          module: "sales_order",
          action: "load_office_agent_options",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/agent/select/${officeId}`,
          method: "POST",
          additionalInfo: {
            page,
            search: inputValue || "",
            officeId: officeId || null
          }
        }, token);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [selectedOffice?.value, token, userId, licenseType, userName]
  );

  type CustomizationField = {
    _id?: string;
    field_name: string;
    field_type: string; // e.g. "text" | "file"
    value: string;
  };

  type ProductCustomizations = {
    _id?: string;
    template_name: string;
    fields: CustomizationField[];
    image_url?: string;
  };

  const [selectedBillingAddress, setSelectedBillingAddress] =
    useState<Addresses | null>(null);

  const [selectedShippingAddress, setSelectedShippingAddress] =
    useState<Addresses | null>(null);
  const [selectedOfficeBillingAddress, setSelectedOfficeBillingAddress] =
    useState<Addresses | null>(null);

  const [selectedOfficeShippingAddress, setSelectedOfficeShippingAddress] =
    useState<Addresses | null>(null);

  const [selectedProducts, setSelectedProducts] = useState<
    {
      variantId: string;
      productId: string;
      ProductName: string;
      sku: string;
      quantity: number;
      unitPrice: number;
      unit_retail_price?: number;
      total: number;
      is_taxable: boolean;
      tax_rule: TaxRule | null;
      tax_group: TaxGroup | null;
      attributes?: Record<string, string>;
      images: any[];
      attributesList?: {
        variantId: string;
        attributes: Record<string, string>;
        sku: string;
        unitPrice: number;
        warehouseId: string;
      }[];
      is_production: boolean;
      product_type?: string;
      customizations?: ProductCustomizations;
      design_id?: string;
      design_name?: string;
      /** Work-drive preview file id (presigned via imageUrlMap), same as agent checkout. */
      preview_url?: string;
      studio_design_id?: string;
      studio_design_name?: string;
      studio_preview_id?: string;
      studio_context_id?: string;
    }[]
  >([]);


  const [selectedShippingMethod, setSelectedShippingMethod] = useState<{
    value: string;
    label: string;
    cost: number;
  } | null>(null);

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const [subTotal, setSubTotal] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);
  const [tax, setTax] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const closePicker = () => setPickerOpen(false);
  const addProductRow = () => {
    setSelectedProducts([
      ...selectedProducts,
      { productId: "", sku: "", quantity: 1, unitPrice: 0, total: 0, is_taxable: false, tax_rule: null, tax_group: null, variantId: "", ProductName: "", is_production: false, images: [] },
    ]);
  };


  // ========================================================================
  // API FUNCTIONS - Parent Hierarchy
  // ========================================================================
  // Fetches parent hierarchy for signageteammember login
  // Used to determine the parent signage firm when logged in as team member
  const fetchParentHierarchy = async () => {
    if (!isSignageTeammember || !token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Find the parent with license_type "SIGNAGE_FIRM"
        const signageFirm = data.parent_chain?.find(
          (parent: any) => parent.license_type === "SIGNAGE_FIRM"
        );

        if (signageFirm) {
          setParentSignageId(signageFirm.id || signageFirm._id);
          setParentSignageName(signageFirm.name);
        }
      }
    } catch (error) {
      safeConsole.error("Error fetching parent hierarchy:", error);
      // Send error email notification
      await handleApiError(error, {
        module: "sales_order",
        action: "fetch_parent_hierarchy",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        method: "GET"
      }, token);
    }
  };

  // Removed fetchBrokerageFirms and featchBrokerageTeammembers - now using AsyncPaginate

  // ===== EFFECTS =====
  // Note: Office loading is now handled by AsyncPaginate with loadOfficeOptions (NEW FLOW)
  // Note: Team member loading is now handled by AsyncPaginate with loadTeamMemberOptions (based on office)
  // Note: Office agents loading is now handled by AsyncPaginate with loadOfficeAgentOptions (based on office)


  // Removed fetchAgents - now using AsyncPaginate with Select2 endpoint

  // Fetch parent hierarchy for signageteammember login
  useEffect(() => {
    if (isSignageTeammember && token) {
      fetchParentHierarchy();
    }
  }, [isSignageTeammember, token, parentSignageId]);

  // Fetch first customer group for SearchBrokerageAgentTeam API
  useEffect(() => {
    if (!token) return;
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/customer-groups?permissions=false`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to fetch customer groups");
        const list = Array.isArray(data) ? data : data?.data ?? [];
        const firstId = list[0]?._id || list[0]?.id || null;
        if (mounted && firstId) setCustomerGroupId(firstId);
      } catch {
        if (mounted) setCustomerGroupId(null);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

useEffect(() => {
    if (!token || !projectIdFromQuery) return;
    let cancelled = false;
    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const getSearchRestoreRef = async () => {
      const deadline = Date.now() + 3000;
      while (!cancelled && Date.now() < deadline) {
        const ref = searchBatRestoreRef.current;
        if (ref) return ref;
        await wait(60);
      }
      return searchBatRestoreRef.current;
    };

    const loadProjectMessageAddresses = async () => {
      try {
        const res = await fetchPublicProjectMessagesByProjectId(projectIdFromQuery, 1, 20);
        if (cancelled || !Array.isArray(res?.data) || res.data.length === 0) return null;
        const latestWithAddresses = res.data.find(
          (m: any) => m?.billing_address || m?.shipping_address
        ) as any;
        if (!latestWithAddresses) return null;

        const billingAddr = latestWithAddresses?.billing_address ?? null;
        const shippingAddr = latestWithAddresses?.shipping_address ?? null;
        const isSame = Boolean(latestWithAddresses?.both_address_same);
        if (!billingAddr && !shippingAddr) return null;

        const prefilled = {
          billing: billingAddr,
          shipping: shippingAddr,
          isSame,
          shippingMethodName: String(latestWithAddresses?.shipping_method?.name || "").trim() || undefined,
        };
        projectMessageAddressesRef.current = prefilled;
        return prefilled;
      } catch (error) {
        safeConsole.warn("Failed to prefill addresses from public project messages:", error);
        return null;
      }
    };

    const run = async () => {
      projectRestoreGuardRef.current = true;
      try {
        const projectData = await fetchProjectById(token, projectIdFromQuery);
        if (cancelled || !projectData) return;
        formik.setFieldValue("po_number", String(projectData.po_number || "").trim(), false);
        await loadProjectMessageAddresses();

        const pn = String((projectData as { project_number?: string }).project_number || "").trim();
        if (pn) setLinkedProjectNumber(pn);

        const email = String(projectData.customer_email || "").trim();
        const phone = withDefaultSignageContactPhone(
          String(
            (projectData as { customer_phone?: string; phone1?: string }).customer_phone ??
              (projectData as { phone1?: string }).phone1 ??
              ""
          ).trim()
        );
        const fn = String(projectData.first_name || "").trim();
        const ln = String(projectData.last_name || "").trim();

        const teamMemberId = String(projectData.team_member_id || "").trim();
        const teamMemberName = String(projectData.team_member_name || "").trim();
        const hasTeamMember =
          !isUnsetMongoId(teamMemberId) || teamMemberName.length > 0;

        const agentId = String(projectData.agent_id || "").trim();
        const agentName = String(projectData.agent_name || "").trim();
        const hasAgent = !isUnsetMongoId(agentId) && agentName.length > 0;
        let rawLt = String(projectData.license_type || "").trim().toUpperCase();
        if (rawLt === "SIGNAGE_TEAM_MEMBER") rawLt = "BROKERAGE_TEAM_MEMBER";
        if (rawLt === "TEAM_MEMBER") rawLt = "BROKERAGE_TEAM_MEMBER";

        const customerIdFromProject = String(projectData.customer_id || "").trim();
        const isEndUserTeamMember =
          rawLt === "BROKERAGE_TEAM_MEMBER" ||
          (!isUnsetMongoId(teamMemberId) && customerIdFromProject === teamMemberId);
        const isEndUserAgent =
          !isEndUserTeamMember &&
          !isUnsetMongoId(agentId) &&
          customerIdFromProject === agentId;

        const contact = {
          fName: isEndUserTeamMember
            ? teamMemberName.split(/\s+/)[0] || fn
            : fn ||
              (isEndUserAgent
                ? agentName.split(/\s+/)[0]
                : hasTeamMember
                  ? teamMemberName.split(/\s+/)[0]
                  : agentName.split(/\s+/)[0]) ||
              "",
          lName: isEndUserTeamMember
            ? teamMemberName.split(/\s+/).slice(1).join(" ") || ln
            : ln ||
              (isEndUserAgent
                ? agentName.split(/\s+/).slice(1).join(" ")
                : hasTeamMember
                  ? teamMemberName.split(/\s+/).slice(1).join(" ")
                  : agentName.split(/\s+/).slice(1).join(" ")) ||
              "",
          email,
          phone,
        };

        let uiType = rawLt;
        if (!uiType) {
          if (hasTeamMember) uiType = "BROKERAGE_TEAM_MEMBER";
          else if (hasAgent) uiType = "AGENT";
          else uiType = "BROKERAGE_FIRM";
        }

        const badge =
          ORDER_TYPE_BADGE_MAP[uiType] ||
          (hasTeamMember ? ORDER_TYPE_BADGE_MAP.BROKERAGE_TEAM_MEMBER : ORDER_TYPE_BADGE_MAP.AGENT);
        setOrderTypeBadge(badge);

        const firmId = String(projectData.brokerage_firm_id || "").trim();
        const firmName = String(projectData.brokerage_firm_name || "").trim();

        if (uiType === "BROKERAGE_FIRM" && firmId) {
          const projectOfficeId = String(projectData.office?._id || "").trim();
          const tmIdForRestore = String(projectData.team_member_id || "").trim();
          const agentIdForRestore = String(projectData.agent_id || "").trim();
          setProjectContactPrefill({
            label: firmName,
            type: "BROKERAGE_FIRM",
            contact,
          });
          const ref = await getSearchRestoreRef();
          if (!cancelled && ref) {
            ref.restoreApplyPick({
              value: firmId,
              label: firmName,
              license_type: "BROKERAGE_FIRM",
              company_name: firmName,
            });
            await wait(80);
            if (!cancelled) {
              if (projectOfficeId && !isUnsetMongoId(projectOfficeId)) {
                await ref.restoreSelectOfficeById(projectOfficeId);
              }
              if (tmIdForRestore && !isUnsetMongoId(tmIdForRestore)) {
                await ref.restoreSelectTeamMemberById(tmIdForRestore);
              }
              if (isEndUserAgent && agentIdForRestore && !isUnsetMongoId(agentIdForRestore)) {
                await ref.restoreSelectAgentById(agentIdForRestore, {
                  label: agentName,
                  firstName: fn,
                  lastName: ln,
                  email,
                  phone,
                });
              } else {
                ref.restoreSyncContact(contact);
              }
              applyProjectMessageAddressesToUi();
            }
          }
          return;
        }

        if (isEndUserTeamMember || (uiType === "BROKERAGE_TEAM_MEMBER" && hasTeamMember)) {
          const tmLabel =
            teamMemberName || [fn, ln].filter(Boolean).join(" ").trim() || "Team member";
          const tmContact = { ...contact };
          const projectOfficeId = String(projectData.office?._id || "").trim();
          const projectOfficeName = String(projectData.office?.officeName || "").trim();
          setProjectContactPrefill({
            label: tmLabel,
            type: "BROKERAGE_TEAM_MEMBER",
            contact: tmContact,
          });
          if (projectOfficeId && !isUnsetMongoId(projectOfficeId)) {
            setSelectedOffice({
              value: projectOfficeId,
              label: projectOfficeName || "Office",
              firstName: projectOfficeName || "Office",
              lastName: "",
              email: "",
              phone: "",
            } as any);
            formik.setFieldValue("officeSelection", projectOfficeId);
          }
          const synthetic = {
            value: teamMemberId,
            label: tmLabel,
            license_type: "BROKERAGE_TEAM_MEMBER",
            email,
            phone,
            brokerage_firm_id: projectData.brokerage_firm_id,
            brokerage_firm_name: projectData.brokerage_firm_name,
            brokerage_name: projectData.brokerage_firm_name,
            firstName: tmContact.fName,
            lastName: tmContact.lName,
            offices:
              projectOfficeId && !isUnsetMongoId(projectOfficeId)
                ? {
                    _id: projectOfficeId,
                    officeName: projectOfficeName || "Office",
                  }
                : undefined,
          };
          const ref = await getSearchRestoreRef();
          if (!cancelled && ref) {
            ref.restoreApplyPick(synthetic);
            await wait(100);
            if (!cancelled) {
              ref.restoreSyncContact(tmContact);
              const oid = String(projectData.office?._id || "").trim();
              if (oid && !isUnsetMongoId(oid)) {
                await ref.restoreSelectOfficeById(oid);
              }
              if (hasAgent && agentId && !isUnsetMongoId(agentId)) {
                await ref.restoreSelectAgentById(agentId, {
                  label: agentName,
                  firstName: agentName.split(/\s+/)[0] || fn,
                  lastName: agentName.split(/\s+/).slice(1).join(" ") || ln,
                  email,
                  phone,
                });
                ref.restoreSyncContact(tmContact);
              }
              const tmForAddr = teamMemberId || synthetic.value;
              if (tmForAddr && !isUnsetMongoId(tmForAddr)) {
                await fetchAddresses({ value: tmForAddr, label: tmLabel });
              }
              applyProjectMessageAddressesToUi();
            }
          }
          return;
        }

        if (!hasAgent && !agentName) {
          safeConsole.warn(
            "Create order: project has no agent/team member to prefill (check license_type and ids)."
          );
          return;
        }

        const label =
          agentName || [contact.fName, contact.lName].filter(Boolean).join(" ").trim();
        const agentLt =
          rawLt === "BROKERAGE_AGENT" ? "BROKERAGE_AGENT" : "AGENT";
        setProjectContactPrefill({
          label,
          type: agentLt,
          contact,
        });
        const syntheticAgent = {
          value: agentId,
          label,
          license_type: agentLt,
          email,
          phone,
          brokerage_firm_id: projectData.brokerage_firm_id,
          brokerage_firm_name: projectData.brokerage_firm_name,
          brokerage_name: projectData.brokerage_firm_name,
          firstName: contact.fName,
          lastName: contact.lName,
        };
        const ref = await getSearchRestoreRef();
        if (!cancelled && ref) {
          ref.restoreApplyPick(syntheticAgent);
          await wait(100);
          if (!cancelled) {
            ref.restoreSyncContact(contact);
            if (agentId && !isUnsetMongoId(agentId)) {
              await fetchAddresses({ value: agentId, label });
            }
            applyProjectMessageAddressesToUi();
          }
        }
      } catch (error) {
        safeConsole.warn("Failed to prefill project contact for create order:", error);
      } finally {
        projectRestoreGuardRef.current = false;
      }
    };

    void run();
    return () => {
      cancelled = true;
      projectRestoreGuardRef.current = false;
    };
  }, [token, projectIdFromQuery]);

  const buyerOptions: { value: string; label: string }[] = [
    { value: "brokerageFirm", label: "Brokerage Firm" },
    { value: "agent", label: "Agent" },
  ];

  // Removed brokerageOptions - now using AsyncPaginate

  // Removed officeOptions - now using AsyncPaginate with loadOfficeOptions (NEW FLOW)

  // Removed officeAgentOptions - now using AsyncPaginate with loadOfficeAgentOptions (based on office)

  // Removed agentOptions - now using AsyncPaginate

  const addressOptions =
    addressObjects !== null
      ? addressObjects?.map((address) => ({
        value: address._id,
        label: `${address?.label}: ${address?.address_details?.addressLine1}, ${address?.address_details?.city}, ${address?.address_details?.state}, ${address?.address_details?.zip ? address?.address_details?.zip : ''}`,
      }))
      : [];

  useEffect(() => {
    addressObjectsMirrorRef.current = addressObjects;
  }, [addressObjects]);

  // Removed teamMemberOptions - now using AsyncPaginate

  const currentOwner =
    selectedOfficeAgent
      ? { id: selectedOfficeAgent.value, name: selectedOfficeAgent.label, type: "agent" as const }
      : selectedAgent
        ? { id: selectedAgent.value, name: selectedAgent.label, type: "agent" as const }
        : selectedOffice
          ? { id: selectedOffice.value, name: selectedOffice.label, type: "office" as const }
          : null;

  // ✅ Separate logic for CouponDiscount createdById (different from currentOwner)
  const effectiveCouponCreatedById = React.useMemo(() => {
    let id = "";
    let source = "";

    // Priority: Office Agent > Team Member (for office path) > Direct Agent > Brokerage Firm
    if (selectedOfficeAgent?.value) {
      // Office Agent path: use agent ID
      id = selectedOfficeAgent.value;
      source = "Office Agent";
    } else if (selectedOffice?.value && selectedTeamMemberOption?.value) {
      // Office selected (without agent): use team member ID (NOT office ID)
      id = selectedTeamMemberOption.value;
      source = "Team Member (via Office)";
    } else if (selectedAgent?.value) {
      // Direct agent path: use agent ID
      id = selectedAgent.value;
      source = "Direct Agent";
    } else if (selectedBrokerageOption?.value) {
      // Brokerage Firm selected (no team member/office/agent)
      id = selectedBrokerageOption.value;
      source = "Brokerage Firm";
    } else {
      id = "";
      source = "User (Fallback)";
    }

    safeConsole.log(`🎟️ [SignageCreate] Coupon createdById: ${id} (from ${source})`);
    return id;
  }, [selectedOfficeAgent, selectedAgent, selectedOffice, selectedTeamMemberOption, selectedBrokerageOption, userId]);


  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const validationSchema = Yup.object({
    // label_of_address: showAddressForm
    //   ? Yup.string().required("Label of address is required")
    //   : Yup.string().notRequired(),

    // firstName: showAddressForm ? firstNameValidation : Yup.string().notRequired(),
    // lastName: showAddressForm ? lastNameValidation : Yup.string().notRequired(),
    // addressLine1: showAddressForm ? addressLine1Validation : Yup.string().notRequired(),
    // city: showAddressForm ? cityValidation : Yup.string().notRequired(),
    // state: showAddressForm ? stateValidation : Yup.string().notRequired(),
    // zipcode: showAddressForm ? zipcodeValidation : Yup.string().notRequired(),
    // country: showAddressForm ? countryValidation : Yup.string().notRequired(),
    // phone: showAddressForm ? phoneValidation : Yup.string().notRequired(),

    userType: Yup.string().required("Buyer type is required"),
    userFirstName: Yup.string().required("First Name is required"),
    userLastName: Yup.string().required("Last Name is required"),
    userEmail: Yup.string().email("Invalid email").required("Email is required"),
    userPhone: Yup.string().required("Phone number is required"),
  });


  const DEFAULT_TEN_ZEROS = DEFAULT_SIGNAGE_CONTACT_PHONE;
  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;

  const formik = useFormik({
    initialValues: {
      userType: "",
      userFirstName: "",
      userLastName: "",
      userEmail: "",
      userPhone: "",
      billingAddressId: "",
      teamMemberId: "",
      teamMemberName: "",
      shippingAddressId: "",
      isSameAsBilling: false,
      productId: "",
      sku: "",
      quantity: 1,
      unitPrice: 0,
      total: 0,
      notes: "",
      label_of_address: "",
      firstName: "",
      lastName: "",
      company: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      country: "",
      state: "",
      zipcode: "",
      phone: DEFAULT_TEN_ZEROS || "",
      shippingMethod: "",
      paymentMethod: "",
      po_number: "",
      latitude: "", // ✅ add this
      longitude: "", // ✅ add this
    },
    validationSchema,

    // ========================================================================
    // FORM SUBMISSION HANDLER
    // ========================================================================
    onSubmit: async (values) => {
      safeConsole.log("Submitting form...", values);

      // ====================================================================
      // VALIDATION - Pre-submit checks
      // ====================================================================
      if (!projectIdFromQuery) {
        const searchValidation = searchBatRestoreRef.current?.validate?.({
          officeId: String(selectedOffice?.value || "").trim(),
          teamMemberId: values.teamMemberId,
        });
        if (searchValidation && !searchValidation.valid) {
          setFormError(
            searchValidation.errors.teamMember ||
              searchValidation.errors.office ||
              searchValidation.errors.search ||
              "Complete office and team member selection."
          );
          return;
        }
      }
      const hasBilling =
        !!selectedBillingAddress ||
        !!selectedOfficeBillingAddress ||
        !!String(values.billingAddressId || "").trim();
      const hasShipping = !!values.shippingAddressId;
      if (!hasBilling && !hasShipping) {
        setFormError("Select Billing Address Or Add Address Any one field is required.");
        return;
      }
      const hasAtLeastOneProduct = (selectedProducts?.length ?? 0) > 0 || (customproducts?.length ?? 0) > 0;
      if (!hasAtLeastOneProduct) {
        setFormError("Add at least one product before saving.");
        return;
      }
      if (!validateAllPurchasedProductFieldsRef.current()) {
        setFormError("Quantity and unit price must be at least 1 for all products.");
        return;
      }
      // if (hasZeroOrNegativeLine) {
      //   setFormError("Cannot place order: a product subtotal became zero or negative after discount.");
      //   return;
      // }

      setFormError(null);
      setFormSubmitting(true);

      safeConsole.log("[SIGNAGE:CREATE_ORDER:SAVE] Recomputing order before save…", { force: true });
      const resolveOk = await recomputeSalesRef.current?.({ force: true });
      if (!resolveOk || !lastResolvedSalesRef.current) {
        safeConsole.warn("[SIGNAGE:CREATE_ORDER:SAVE] Recompute before save failed", {
          resolveOk,
          hasLastResolved: !!lastResolvedSalesRef.current,
          reason: !resolveOk
            ? "recomputeSalesOrder returned false"
            : "lastResolvedSalesRef is null (stale request or never resolved)",
        });
        showError("Could not recalculate order before save. Please try again.");
        setFormSubmitting(false);
        return;
      }
      safeConsole.log("[SIGNAGE:CREATE_ORDER:SAVE] Recompute before save succeeded", {
        orderNumber: lastResolvedSalesRef.current?.order_number,
        totalAmount: lastResolvedSalesRef.current?.total_amount,
      });

      let orderPayload: Record<string, unknown>;
      try {
        const built = await buildOrderPayloadForApiRef.current?.(values);
        if (!built) {
          showError("Order payload builder is not ready.");
          setFormSubmitting(false);
          return;
        }
        orderPayload = overlaySalesSubmitFromResolved(built, lastResolvedSalesRef.current);
      } catch (e) {
        safeConsole.error(e);
        showError(e instanceof Error ? e.message : "Failed to prepare order");
        setFormSubmitting(false);
        return;
      }

      const op = orderPayload as Record<string, any>;
      const finalShippingAddress = op.shipping_address;
      const finalBillingAddress = op.billing_address;
      const agent_name = op.agent_name;
      const products = op.items;
      const grandTotalNow = Number(op.total_amount ?? 0);

      // ====================================================================
      // API REQUEST - Create Order
      // ====================================================================
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/orders`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(orderPayload),
        });

        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const userEmailError = typeof result?.userEmail === "string" ? result.userEmail : "";
          const userPhoneError = typeof result?.userPhone === "string" ? result.userPhone : "";

          if (userEmailError || userPhoneError) {
            if (userEmailError) {
              formik.setFieldError("userEmail", userEmailError);
              formik.setFieldTouched("userEmail", true, false);
            }
            if (userPhoneError) {
              formik.setFieldError("userPhone", userPhoneError);
              formik.setFieldTouched("userPhone", true, false);
            }
            return;
          }

          const message = result?.error || result?.message || "Failed to save data";
          safeConsole.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }

        setOrder(result);

        const createdOrderId = String(result?.id || result?._id || "").trim();
        const pendingFiles = pendingInstructionFilesRef.current.map((x) => x.file);
        if (createdOrderId && pendingFiles.length > 0 && token) {
          try {
            await uploadOrderDocumentsToOrder(token, createdOrderId, pendingFiles);
          } catch (uploadErr) {
            safeConsole.warn("upload-documents-to-order failed:", uploadErr);
            showError(
              uploadErr instanceof Error
                ? `Order created, but attachments failed to upload: ${uploadErr.message}`
                : "Order created, but attachments failed to upload."
            );
          }
        }

        localStorage.removeItem("draft_unique_id");

        safeNavigate(`${baseRoute}/orderDetails/${result.id}`);
        showSuccess("Order created successfully!");

        void runSignageCreateOrderPostCreateEffects({
          result,
          op,
          token: token || "",
          linkedProjectId,
          projectIdFromQuery,
          userClearedProject: userClearedProjectRef.current,
          finalShippingAddress,
          finalBillingAddress,
          agent_name,
          products,
          grandTotalNow,
          grandTotal,
          values,
          isSignageTeammember,
          parentSignageName,
          userName,
          userId,
          baseRoute,
          selectedOfficeAgent,
          selectedOffice,
          selectedTeamMemberOption,
          selectedAgent,
          selectedBrokerageOption,
          effectiveCouponCreatedById,
        });
      } catch (error: any) {
        safeConsole.error(error);
        showError(`Error: ${error.message}`);
        // Send error email notification (file name and line number are automatically extracted)
        await handleApiError(error, {
          module: "sales_order",
          action: "create_order",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/orders`,
          method: "POST",
          requestBody: orderPayload,
          additionalInfo: {
            buyerType: values.userType,
            hasProducts: selectedProducts.length > 0,
            orderType: "sales"
          }
        }, token);
      } finally {
        setFormSubmitting(false);
      }
    },



  });

  formikValuesRef.current = formik.values;

  useEffect(() => {
    const pid = String(linkedProjectId || "").trim();
    if (!token) return;
    if (!pid || String(projectIdFromQuery || "").trim()) {
      if (!pid && !String(projectIdFromQuery || "").trim()) {
        formik.setFieldValue("po_number", "", false);
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const projectData = await fetchProjectById(token, pid);
        if (cancelled || !projectData) return;
        formik.setFieldValue("po_number", String(projectData.po_number || "").trim(), false);
      } catch {
        /* project PO is optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, linkedProjectId, projectIdFromQuery]);

  useEffect(() => {
    if (!formik.values.productId) return; // Ensure productId is set

    const selectedProductt = ProductObjs?.find(
      (item) => item.product_id === formik.values.productId
    );

    setSelectedProductItem(selectedProductt || null);

    if (selectedProductt) {
      formik.setFieldValue("sku", selectedProductt?.product?.sku);
      // formik.setFieldValue("quantity", selectedProductt.quantity);
      formik.setFieldValue(
        "unitPrice",
        selectedProductt?.product?.retail_price
      );
      // formik.setFieldValue("total", selectedProductt.firstName);
    }
  }, [formik.values.productId, ProductObjs]); // Runs when productId or ProductObjs change

  useEffect(() => {
    const fetchProducts = async () => {
      const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;
      const signageName = (isSignageTeammember && parentSignageName) ? parentSignageName : userName;
      if (!signageId) {
        setError("User ID is not available");
        return;
      }
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/products`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        // Ensure ProductObjs is always an array
        const productsArray = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setProductObjs(productsArray);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
        setProductObjs([]); // Set empty array on error
        // Send error email notification
        const enduserid = effectiveCouponCreatedById || undefined;
        await handleApiError(err, {
          module: "sales_order",
          action: "fetch_products",
          userId: signageId || undefined,
          EnduserId: enduserid || undefined,
          licenseType: licenseType || undefined,
          userName: signageName || undefined,
          url: `${process.env.REACT_APP_API_URL}/signage/${signageId}/inventory/products`,
          method: "GET"
        }, token);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [token]);

  const idOf = (x?: any): string => {
    if (!x) return "";
    if (typeof x === "string") return x;
    if (typeof x?.url === "string") return x.url;
    return "";
  };
  const allImageIds = React.useMemo(() => {
    const ids = new Set<string>();
    (selectedProducts || []).forEach((row) => {
      const firstId = idOf(row?.images?.[0]);
      if (firstId) ids.add(firstId);
      const pv = String(row?.preview_url || row?.studio_preview_id || "").trim();
      if (pv) ids.add(pv);
      const fields = row?.customizations?.fields || [];
      fields.forEach((f: any) => {
        if (f?.field_type === "file" && typeof f?.value === "string" && f.value.trim()) {
          ids.add(f.value.trim());
        }
      });
    });

    return Array.from(ids);
  }, [selectedProducts]);

  const dataForMap = React.useMemo(() => {
    if (allImageIds.length === 0) return [];
    return [
      {
        product: {
          variants: [
            { images: allImageIds.map((url) => ({ url })) },
          ],
        },
      },
    ];
  }, [allImageIds]);
  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);
  const buildImageUrl = (raw?: string) => {
    if (!raw) return "";
    const cleaned = raw.replace(/\\/g, "/");
    if (/^https?:\/\//i.test(cleaned)) return cleaned;
    const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
    const path = cleaned.replace(/^\/+/, "");
    return `${base}/${encodeURI(path)}`;
  };

  useEffect(() => {
    // console.log("UserType :", formik.values.userType);
  }, [formik.values.userType, selectedOffice, selectedAgent]);

  const handleShowAddAddress = () => {
    setShowAddressForm(true);
  };

  const resetBuyerAddressSelections = () => {
    if (projectRestoreGuardRef.current) return;
    addressOwnerTokenRef.current += 1;
    formik.setFieldValue("billingAddressId", "", false);
    formik.setFieldValue("shippingAddressId", "", false);
    formik.setFieldTouched("billingAddressId", false, false);
    formik.setFieldTouched("shippingAddressId", false, false);
    setAddressObjects([]);
    setSelectedBillingAddress(null);
    setSelectedShippingAddress(null);
    setSelectedOfficeBillingAddress(null);
    setSelectedOfficeShippingAddress(null);
    setAddressSelectKey((k) => k + 1);
  };

  const fetchAddresses = async (user: any) => {
    const requestToken = addressOwnerTokenRef.current;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user-addresses/${user?.value}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Failed to fetch addresses: ${response.statusText}`);
      }

      const data = await response.json();
      if (requestToken !== addressOwnerTokenRef.current) {
        return [];
      }
      const list = mergeProjectPrefilledAddresses(Array.isArray(data) ? data : []);
      setAddressObjects(list);
      return list;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
      setError(errorMessage);

      // Send error email notification and log to file (file name and line number are automatically extracted)
      await handleApiError(err, {
        module: "sales_order",
        action: "fetch_addresses",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/user-addresses/${user?.value}`,
        method: "GET",
        additionalInfo: {
          userId: user?.value || null,
          userName: user?.label || null
        }
      }, token);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const applyStudioOfficeSelection = (office: { id: string; label: string; company_name?: string; office_count?: number }) => {
    if (!office?.id) return;
    const selected = {
      value: office.id,
      label: office.label,
      firstName: office.label,
      lastName: "",
      email: "",
      phone: "",
    };

    const hasTopLevelTeamMember =
      !!formik.values.teamMemberId || !!teamMemberFromContinueRef.current?.value;

    if (!hasTopLevelTeamMember) {
      teamMemberFromContinueRef.current = null;
      setSelectedTeamMemberOption(null);
      formik.setFieldValue("teamMemberId", "");
      formik.setFieldValue("teamMemberName", "");
    }
    setSelectedOfficeAgent(null);
    setSelectedAgent(null);
    resetBuyerAddressSelections();
    if (!studioRestoreGuardRef.current) {
      setSelectedProducts([]);
    }

    setSelectedOffice(selected as any);
    formik.setFieldValue("officeSelection", office.id);
  };

  const applyStudioTeamMemberSelection = (
    member: {
      id: string;
      label: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    },
    /** Used when React has not flushed brokerage/office yet (e.g. Studio URL restore). */
    addressOwnerOverride?: { value: string; label: string } | null
  ) => {
    if (!member?.id) return;
    const s: any = {
      value: member.id,
      label: member.label,
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      phone: member.phone,
      offices: [],
    };
    teamMemberFromContinueRef.current = { value: s.value, label: s.label };
    setSelectedTeamMemberOption(s);
    formik.setFieldValue("teamMemberId", s.value);
    formik.setFieldValue("teamMemberName", s.label);
    formik.setFieldValue("userEmail", s.email);
    formik.setFieldValue("userPhone", withDefaultSignageContactPhone(s.phone));
    formik.setFieldValue("userFirstName", s.firstName);
    formik.setFieldValue("userLastName", s.lastName);

    setSelectedOfficeAgent(null);
    formik.setFieldValue("officeAgentSelection", "");
    formik.setFieldValue("agentSelection", "");

    resetBuyerAddressSelections();

    const addrUser: any =
      addressOwnerOverride && addressOwnerOverride.value
        ? addressOwnerOverride
        : selectedOffice?.value
          ? { value: selectedOffice.value, label: selectedOffice.label }
          : selectedBrokerageOption
            ? {
              value: (selectedBrokerageOption as any).value,
              label: (selectedBrokerageOption as any).label,
            }
            : null;
    if (addrUser) {
      fetchAddresses(addrUser);
    }
  };

  const applyBuyerSearchContinuePayload = (payload: {
    type: string;
    item: any;
    contact: { fName: string; lName: string; email: string; phone: string };
  }) => {
    const { type: licenseTypePayload, item, contact: contactData } = payload || {};
    const badge = ORDER_TYPE_BADGE_MAP[licenseTypePayload] || ORDER_TYPE_BADGE_MAP.AGENT;
    setOrderTypeBadge(badge);
    const c = contactData || {};
    const resolvedUserPhone = withDefaultSignageContactPhone(
      String(c.phone ?? "").trim() || primaryPhoneFromUserLike(item as Record<string, unknown>)
    );
    formik.setFieldValue("userFirstName", c.fName ?? "");
    formik.setFieldValue("userLastName", c.lName ?? "");
    formik.setFieldValue("userEmail", c.email ?? "");
    formik.setFieldValue("userPhone", resolvedUserPhone);
    const opt = {
      value: item?.value ?? "",
      label: item?.label ?? "",
      firstName: c.fName ?? "",
      lastName: c.lName ?? "",
      email: item?.email ?? c.email ?? "",
      phone: resolvedUserPhone,
      brokerageFirmId:
        (item as any)?.brokerage_firm_id ??
        (item as any)?.brokerageFirmId ??
        (item as any)?.brokerage_id ??
        null,
      brokerageFirmName:
        (item as any)?.brokerage_firm_name ??
        (item as any)?.brokerageFirmName ??
        (item as any)?.brokerage_name ??
        null,
    };
    setSelectedOffice(null);
    setSelectedOfficeAgent(null);
    if (!projectRestoreGuardRef.current) {
      setAddressObjects([]);
      setSelectedBillingAddress(null);
      setSelectedShippingAddress(null);
      setSelectedOfficeBillingAddress(null);
      setSelectedOfficeShippingAddress(null);
    }
    if (licenseTypePayload === "BROKERAGE_FIRM") {
      teamMemberFromContinueRef.current = null;
      formik.setFieldValue("userType", "brokerageFirm");
      formik.setFieldValue("brokerageFirm", item?.value ?? "");
      formik.setFieldValue("brokerageFirmName", item?.label ?? "");
      formik.setFieldValue("teamMemberId", "");
      formik.setFieldValue("teamMemberName", "");
      formik.setFieldValue("officeSelection", "");
      formik.setFieldValue("agentSelection", "");
      setSelectedBrokerageOption(opt);
      setSelectedTeamMemberOption(null);
      setSelectedAgent(null);
      setBuyerType(item?.value ?? "");
      const addrUser: any = { value: item?.value ?? "", label: item?.label ?? "" };
      fetchAddresses(addrUser);
    } else if (licenseTypePayload === "BROKERAGE_TEAM_MEMBER" || licenseTypePayload === "TEAM_MEMBER") {
      const tmId = (item?.value ?? (item as any)?.id ?? (item as any)?._id) ?? "";
      const tmLabel = (item?.label ?? (item as any)?.name ?? `${(item as any)?.firstName ?? ""} ${(item as any)?.lastName ?? ""}`.trim()) || "";
      teamMemberFromContinueRef.current = tmId ? { value: tmId, label: tmLabel } : null;
      const officeIdFromItem = String(
        (item as any)?.offices?._id ??
        (item as any)?.office?._id ??
        ""
      ).trim();
      const officeNameFromItem = String(
        (item as any)?.offices?.officeName ??
        (item as any)?.office?.officeName ??
        (item as any)?.offices?.name ??
        (item as any)?.office?.name ??
        ""
      ).trim();
      formik.setFieldValue("userType", "brokerageFirm");
      formik.setFieldValue("teamMemberId", tmId);
      formik.setFieldValue("teamMemberName", tmLabel);
      formik.setFieldValue("officeSelection", officeIdFromItem || "");
      formik.setFieldValue("agentSelection", "");
      setSelectedTeamMemberOption({ ...opt, value: tmId, label: tmLabel, offices: [] });
      if (officeIdFromItem) {
        const defaultOffice = {
          value: officeIdFromItem,
          label: officeNameFromItem || "Office",
          firstName: officeNameFromItem || "Office",
          lastName: "",
          email: "",
          phone: "",
        };
        setSelectedOffice(defaultOffice as any);
      } else {
        setSelectedOffice(null);
      }
      if (opt.brokerageFirmId) {
        const b: any = {
          value: opt.brokerageFirmId,
          label: opt.brokerageFirmName || "",
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
        };
        setSelectedBrokerageOption(b);
        formik.setFieldValue("brokerageFirm", b.value);
        formik.setFieldValue("brokerageFirmName", b.label);
      } else {
        setSelectedBrokerageOption(null);
      }
      setSelectedAgent(null);
      setBuyerType("");
    } else if (licenseTypePayload === "OFFICE") {
      teamMemberFromContinueRef.current = null;
      formik.setFieldValue("userType", "brokerageFirm");
      formik.setFieldValue("teamMemberId", "");
      formik.setFieldValue("teamMemberName", "");
      formik.setFieldValue("agentSelection", "");
      formik.setFieldValue("officeSelection", item?.value ?? "");
      if (opt.brokerageFirmId) {
        const b: any = {
          value: opt.brokerageFirmId,
          label: opt.brokerageFirmName || "",
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
        };
        setSelectedBrokerageOption(b);
        formik.setFieldValue("brokerageFirm", b.value);
        formik.setFieldValue("brokerageFirmName", b.label);
      } else {
        setSelectedBrokerageOption(null);
      }
      setSelectedAgent(null);
      setSelectedTeamMemberOption(null);
      setSelectedOffice(opt);
      setBuyerType("");
      fetchAddresses(opt);
    } else {
      teamMemberFromContinueRef.current = null;
      formik.setFieldValue("userType", "agent");
      formik.setFieldValue("agentSelection", item?.value ?? "");
      if (opt.brokerageFirmId) {
        const b: any = {
          value: opt.brokerageFirmId,
          label: opt.brokerageFirmName || "",
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
        };
        setSelectedBrokerageOption(b);
        formik.setFieldValue("brokerageFirm", b.value);
        formik.setFieldValue("brokerageFirmName", b.label);
      } else {
        formik.setFieldValue("brokerageFirm", "");
        formik.setFieldValue("brokerageFirmName", "");
        setSelectedBrokerageOption(null);
      }
      formik.setFieldValue("teamMemberId", "");
      formik.setFieldValue("teamMemberName", "");
      formik.setFieldValue("officeSelection", "");
      setSelectedAgent(opt);
      setSelectedTeamMemberOption(null);
      setBuyerType("");
    }
    if (item?.value && (licenseTypePayload === "AGENT" || licenseTypePayload === "BROKERAGE_AGENT")) {
      fetchAddresses(opt);
    }
  };


  useEffect(() => {
    if (selectedOfficeAgent) {
      fetchAddresses(selectedOfficeAgent);
    } else if (selectedAgent) {
      fetchAddresses(selectedAgent);
    } else if (selectedOffice) {
      fetchAddresses(selectedOffice);
    }
  }, [selectedAgent, selectedOfficeAgent, selectedOffice, token]);

  // helper


  // ========================================================================
  // EVENT HANDLERS - Product Quantity Change
  // ========================================================================
  // Handles quantity input changes for products in the table
  // When quantity changes, fetches new effective price from API
  const handleQuantityChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    // Allow empty string for backspace/delete
    if (rawValue === "") {
      const updated = [...selectedProducts];
      const existing = updated[index] || { unitPrice: 0 };
      updated[index] = { ...existing, quantity: 0, total: 0 };
      setSelectedProducts(updated);
      return;
    }

    const quantity = Number(rawValue);

    // Prevent negative values
    if (quantity < 0) {
      return;
    }

    const updated = [...selectedProducts];
    const existing = updated[index] || { unitPrice: 0 };
    const key = `${existing.productId}:${existing.variantId}`;
    const manualUnit = manualUnitPriceMap[key];

    // Update quantity and recalculate total (keep manual unit price when user overrode it)
    const unitForTotal = manualUnit !== undefined ? manualUnit : existing.unitPrice;
    updated[index] = { ...existing, quantity, total: unitForTotal * quantity };
    setSelectedProducts(updated);

    // Only clear manual override when qty changes without a user-edited unit price
    if (manualUnit === undefined) {
      setManualUnitPriceMap(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }

    // Recompute effective price for this row with new quantity
    if (quantity > 0) {
      if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
      recomputeTimerRef.current = setTimeout(() => {
        void recomputeSalesRef.current?.();
      }, 300);
    }
  };

  const removeProductRow = (index: number) => {
    const removed = selectedProducts[index];
    const updatedProducts = selectedProducts?.filter((_, i) => i !== index);
    setSelectedProducts(updatedProducts);
    if (removed) {
      const errorKey = purchasedProductErrorKey(
        "catalog",
        `${removed.productId}:${removed.variantId}`
      );
      setPurchasedProductFieldErrors((prev) => {
        const next = { ...prev };
        delete next[errorKey];
        return next;
      });
    }
  };

  useEffect(() => {
    const fetchShippingCosts = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/shipping-costs`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        // console.log("Shipping Costs Response:", data);
        setShippingCosts(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
        setError(errorMessage);
        // Send error email notification
        await handleApiError(err, {
          module: "sales_order",
          action: "fetch_shipping_costs",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/shipping-costs`,
          method: "GET",
          additionalInfo: {
            parentSignageId: parentSignageId || null
          }
        }, token);
      } finally {
        setLoading(false);
      }
    };

    fetchShippingCosts();
  }, [token, parentSignageId, userId, licenseType, userName]);

  // Fetch general settings to show/hide Custom Item button
  useEffect(() => {
    if (!token) return;
    const url = `${process.env.REACT_APP_API_URL}/settings/general?permissions=false`;
    fetch(url, { method: "GET", headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error("Failed to load settings")))
      .then((data: { results?: { key: string; enabled: boolean }[] }) => {
        const list = Array.isArray(data?.results) ? data.results : [];
        const custom = list.find((s) => s.key === GENERAL_SETTINGS_KEYS.CUSTOM_PRODUCT);
        setCustomProductSettingEnabled(custom?.enabled ?? false);
      })
      .catch(() => setCustomProductSettingEnabled(false));
  }, [token]);

  const handleSaveDraft = () => {
    const hasBilling =
      !!selectedBillingAddress ||
      !!selectedOfficeBillingAddress ||
      !!String(formik.values.billingAddressId || "").trim();
    const hasShipping = !!formik.values.shippingAddressId;
    const hasAtLeastOneProduct = (selectedProducts.length > 0) || (customproducts && customproducts.length > 0);

    if (!hasBilling && !hasShipping) {
      setFormError("Select Billing Address Or Add Address Any one field is required.");
      return;
    }

    if (!hasAtLeastOneProduct) {
      setFormError("Add at least one product before saving.");
      return;
    }
    setFormError(null);
    // now submit the form manually or trigger formik.submitForm()
    formik.submitForm();
  };
  const handleAddFromPicker = (payload: any) => {
    // console.log("Picker payload Add incomplete:", payload);

    let shouldClosePicker = true;
    try {
      if (!payload || !payload.product_id || !payload.variant_id) {
        safeConsole.warn("Picker payload incomplete:", payload);
        setPickerOpen(false);
        return;
      }

      const incomingPid = String(payload.product_id).trim();
      const incomingVid = String(payload.variant_id).trim();
      const alreadyAdded = (selectedProducts || []).some((r: any) => {
        const pid = String(r?.productId ?? r?.product_id ?? "").trim();
        const vid = String(r?.variantId ?? r?.variant_id ?? "").trim();
        if (!pid || !vid) return false;
        return pid === incomingPid && vid === incomingVid;
      });
      // if (alreadyAdded) {
      //   showError("This product is already added.");
      //   shouldClosePicker = false;
      //   return;
      // }

      const productObj = ProductObjs?.find(p => p.product_id === payload.product_id);
      if (!productObj) {
        safeConsole.warn("Product not found for product_id:", payload.product_id);
        setPickerOpen(false);
        return;
      }

      const variants = productObj.product?.variants || [];
      const selectedVariant = variants.find(v => v.variant_id === payload.variant_id);
      if (!selectedVariant) {
        safeConsole.warn("Variant not found for variant_id:", payload.variant_id);
        setPickerOpen(false);
        return;
      }
      // console.log("Selected Variant:", selectedVariant);
      // console.log("productObj:", productObj);
      // Build attributesList entry same as your table expects
      const attributesList = [{
        variantId: selectedVariant.variant_id,
        attributes: selectedVariant.attributes ?? {},
        sku: selectedVariant.sku,
        unitPrice: Number(
          payload.unit_retail_price ??
          selectedVariant.retail_price ??
          productObj.product?.retail_price ??
          0
        ),
        warehouseId: selectedVariant.inventory?.[0]?.warehouse_id || "",
      },
      ];

      const quantity = Number(payload.quantity ?? 1);
      const unitPrice = Number(
        payload.unit_retail_price ??
        selectedVariant.retail_price ??
        productObj.product?.retail_price ??
        0
      );
      const total = unitPrice * quantity;

      const imgs = pickImages(selectedVariant, productObj, payload.images);
      const firstImgPath = (imgs?.[0] && (typeof imgs[0] === "string" ? imgs[0] : (imgs[0] as any).url)) as string | undefined;

      const isTaxable = resolveCatalogProductLineTaxable({
        pickerPayload: payload as Record<string, unknown>,
        catalogProduct: productObj.product as Record<string, unknown>,
      });
      const isProduction =
        typeof payload.is_production === "boolean"
          ? payload.is_production
          : !!productObj.product?.is_production;

      setProductObjs((prev) => {
        const idx = prev.findIndex((o) => o.product_id === payload.product_id);
        if (idx < 0) return prev;
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          product: {
            ...(next[idx].product || {}),
            is_taxable: isTaxable,
            is_production: isProduction,
          },
        };
        return next;
      });

      const newRow = {
        productId: payload.product_id,
        variantId: payload.variant_id,
        ProductName: selectedVariant.variant_name || productObj.product?.name || "",
        sku: selectedVariant.sku || productObj.product?.sku || "",
        quantity,
        unitPrice,
        total,
        is_taxable: isTaxable,
        tax_rule: productObj.product?.tax_rule || null,
        tax_group: productObj.product?.tax_group || null,
        attributesList,
        is_production: isProduction,
        product_type: productObj.product?.product_type,
        customizations: payload.customizations ?? undefined,
        images: imgs,                                       // keep for later use
        image_url: buildImageUrl(firstImgPath),
        ...(payload.design_id
          ? {
              design_id: String(payload.design_id),
              design_name: String(payload.design_name ?? ""),
              preview_url: String(payload.preview_url ?? ""),
            }
          : {}),
      };
      const updated = [...selectedProducts, newRow];
      setSelectedProducts(updated);
    } finally {
      if (shouldClosePicker) setPickerOpen(false);
    }
  };
  const canShowAdd = Boolean(selectedAgent || selectedOffice || selectedOfficeAgent); // same as your first table
  const pickerExistingLineKeys = React.useMemo(
    () =>
      (selectedProducts || [])
        .map((r) => {
          const pid = String(r.productId ?? "").trim();
          const vid = String(r.variantId ?? "").trim();
          return pid && vid ? `${pid}:${vid}` : "";
        })
        .filter((k): k is string => Boolean(k)),
    [selectedProducts]
  );
  const hasRows = (selectedProducts?.length || 0) + (customproducts?.length || 0) > 0;
  const colCount = 8;

  const removeCustomProduct = (index: number) => {
    setCustomproducts((prev) => prev.filter((_, i) => i !== index));
    setPurchasedProductFieldErrors((prev) => {
      const next: Record<string, PurchasedProductFieldErrors> = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (!key.startsWith("custom:")) {
          next[key] = value;
          return;
        }
        const customIdx = Number(key.slice("custom:".length));
        if (customIdx < index) next[key] = value;
        else if (customIdx > index) {
          next[purchasedProductErrorKey("custom", String(customIdx - 1))] = value;
        }
      });
      return next;
    });
    scheduleSalesRecomputeRef.current?.();
  };

  const validateAllPurchasedProductFields = React.useCallback((): boolean => {
    const nextErrors: Record<string, PurchasedProductFieldErrors> = {};
    const setErr = (key: string, field: keyof PurchasedProductFieldErrors, msg: string) => {
      nextErrors[key] = { ...nextErrors[key], [field]: msg };
    };

    for (const product of selectedProducts || []) {
      const rowKey = `${product.productId}:${product.variantId}`;
      const errorKey = purchasedProductErrorKey("catalog", rowKey);
      const qtyErr = validatePurchasedQuantityValue(product.quantity);
      if (qtyErr) setErr(errorKey, "quantity", qtyErr);
      const unitRaw =
        manualUnitPriceMap[rowKey] !== undefined
          ? String(manualUnitPriceMap[rowKey])
          : Number(product.unitPrice || 0) > 0
            ? String(product.unitPrice)
            : "";
      const unitErr = validatePurchasedUnitPriceValue(unitRaw);
      if (unitErr) setErr(errorKey, "unitPrice", unitErr);
    }

    (customproducts || []).forEach((cp, idx) => {
      const errorKey = purchasedProductErrorKey("custom", String(idx));
      const qtyErr = validatePurchasedQuantityValue(cp.quantity);
      if (qtyErr) setErr(errorKey, "quantity", qtyErr);
      const unitErr = validatePurchasedUnitPriceValue(cp.Price);
      if (unitErr) setErr(errorKey, "unitPrice", unitErr);
    });

    setPurchasedProductFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [selectedProducts, customproducts, manualUnitPriceMap]);

  validateAllPurchasedProductFieldsRef.current = validateAllPurchasedProductFields;

  const updateCustomProduct = (index: number, updates: { quantity?: number; Price?: number }) => {
    setCustomproducts((prev) =>
      prev.map((p, i) => (i === index ? { ...p, ...updates } : p))
    );
    scheduleSalesRecomputeRef.current?.();
  };

  type ResetLevel = "userType" | "brokerage" | "teamMember" | "office" | "officeAgent" | "agent";

  // Clears just the name/email/phone + addresses
  const clearPersonAndAddresses = () => {
    formik.setFieldValue("userFirstName", "");
    formik.setFieldValue("userLastName", "");
    // formik.setFieldValue("userEmail", "");
    // formik.setFieldValue("userPhone", "");
    formik.setFieldValue("billingAddressId", "");
    formik.setFieldValue("shippingAddressId", "");
    formik.setFieldValue("isSameAsBilling", true);

    setAddressObjects([]);
    setSelectedBillingAddress(null);
    setSelectedShippingAddress(null);
    setSelectedOfficeBillingAddress(null);
    setSelectedOfficeShippingAddress(null);
  };

  // Clears a specific select (formik + local)
  const clearBrokerage = () => {
    formik.setFieldValue("brokerageFirm", "");
    setSelectedBrokerageOption(null);
    setSelectedTeamMemberOption(null);
    setOffices([]);
  };

  const clearTeamMember = () => {
    teamMemberFromContinueRef.current = null;
    formik.setFieldValue("teamMemberId", "");
    setSelectedTeamMemberOption(null);
    setOffices([]);          // team member usually defines offices scope
  };

  const clearOffice = () => {
    formik.setFieldValue("officeSelection", "");
    setSelectedOffice(null);
    setOfficeAgents([]);
  };

  const clearOfficeAgent = () => {
    formik.setFieldValue("officeAgentSelection", "");
    setSelectedOfficeAgent(null);
  };

  const clearAgent = () => {
    formik.setFieldValue("agentSelection", "");
    setSelectedAgent(null);
  };

  const resetBelow = (level: ResetLevel) => {
    switch (level) {
      case "userType":
        // Clear ALL downstream
        clearBrokerage();
        clearTeamMember();
        clearOffice();
        clearOfficeAgent();
        clearAgent();
        clearPersonAndAddresses();
        break;

      case "brokerage":
        // Changing brokerage wipes team member → office → office agent → agent → person/addresses
        clearTeamMember();
        clearOffice();
        clearOfficeAgent();
        clearAgent();
        clearPersonAndAddresses();
        break;

      case "teamMember":
        // Changing team member wipes office → office agent → agent → person/addresses
        clearOffice();
        clearOfficeAgent();
        clearAgent();
        clearPersonAndAddresses();
        break;

      case "office":
        // Changing office wipes office agent → person/addresses
        clearOfficeAgent();
        // clearPersonAndAddresses();
        break;

      // case "officeAgent":
      //   // Changing office agent only wipes person/addresses
      //   clearPersonAndAddresses();
      //   break;

      // case "agent":
      //   // Agent flow is disjoint from office flow — clear office side + person/addresses
      //   clearOffice();
      //   clearOfficeAgent();
      //   clearPersonAndAddresses();
      //   break;
    }
  };

  useEffect(() => {
    const id = String(formik.values.billingAddressId || "").trim();
    if (!id) return;
    const prefilledBilling = projectMessageAddressesRef.current?.billing;
    const found =
      findSalesOrderAddressById(addressObjects, id) ||
      (prefilledBilling && resolveSalesOrderAddressId(prefilledBilling) === id
        ? prefilledBilling
        : null);
    if (!found) return;
    setAddressObjects((prev) => upsertAddressInList(prev, found));
    // Keep both buckets so office+team-member submit cannot drop billing.
    setSelectedBillingAddress(found);
    setSelectedOfficeBillingAddress(found);
  }, [formik.values.billingAddressId, addressObjects]);

  useEffect(() => {
    const id = String(formik.values.shippingAddressId || "").trim();
    if (!id) {
      setSelectedShippingAddress(null);
      setSelectedOfficeShippingAddress(null);
      return;
    }
    const found = (addressObjects || []).find((a: any) => String(a._id) === id);
    if (!found) {
      const prefilledShipping = projectMessageAddressesRef.current?.shipping;
      if (prefilledShipping && String(prefilledShipping._id) === id) {
        setAddressObjects((prev) => upsertAddressInList(prev, prefilledShipping));
        setSelectedShippingAddress(prefilledShipping);
        return;
      }
      if (projectRestoreGuardRef.current) return;
      formik.setFieldValue("shippingAddressId", "", false);
      setSelectedShippingAddress(null);
      setSelectedOfficeShippingAddress(null);
      return;
    }
    const isOffice = !selectedOfficeAgent && !selectedAgent && !!selectedOffice;
    if (isOffice) {
      setSelectedOfficeShippingAddress(found);
      setSelectedShippingAddress(null);
    } else {
      setSelectedShippingAddress(found);
      setSelectedOfficeShippingAddress(null);
    }
  }, [
    formik.values.shippingAddressId,
    addressObjects,
    selectedOfficeAgent,
    selectedAgent,
    selectedOffice,
  ]);


  // --- 3) Memo: which address should UPS use? (shipping if set; else billing when "same as") ---
  const shippingAddressForRates = React.useMemo(() => {
    return formik.values.isSameAsBilling
      ? (selectedBillingAddress || selectedOfficeBillingAddress || null)
      : (selectedShippingAddress || selectedOfficeShippingAddress || null);
  }, [
    formik.values.isSameAsBilling,
    selectedBillingAddress, selectedOfficeBillingAddress,
    selectedShippingAddress, selectedOfficeShippingAddress
  ]);

  const selectedProductsWithDims = useMemo(() => {
    const catalogItems = (selectedProducts || []).map(sp => {
      const product = (ProductObjs || [])
        .find(po => String(po.product_id) === String(sp.productId))?.product;
      const variant = product?.variants?.find((v: any) =>
        String(v.variant_id) === String(sp.variantId)
      );

      // strictly variant-level dims
      const dims = variant?.package_dimensions || {
        length: 0, width: 0, height: 0, weight: 0, weight_type: "LBS",
      };

      const lineName =
        String(sp.ProductName || "").trim() ||
        String((product as any)?.name || (product as any)?.product_name || "").trim() ||
        String((variant as any)?.name || (variant as any)?.variant_name || "").trim() ||
        "Product";
      const lineSku =
        String(sp.sku || "").trim() ||
        String((variant as any)?.sku || (product as any)?.sku || "").trim();

      return {
        productId: sp.productId || "000000000000000000000000",
        variantId: sp.variantId || "000000000000000000000000",
        quantity: sp.quantity,
        name: lineName,
        sku: lineSku,
        package_dimensions: dims,
      };
    });

    const customItems = (customproducts || []).map((cp) => ({
      productId: cp.product_id,
      variantId: cp.variant_id,
      quantity: cp.quantity,
      name: String(cp.item_name || "").trim() || "Custom Item",
      sku: String(cp.sku || "").trim(),
      package_dimensions: {
        length: Number(cp.length) || 0,
        width: Number(cp.width) || 0,
        height: Number(cp.height) || 0,
        weight: Number(cp.weight) || 0,
        weight_type: "LBS" as const,
      },
    }));

    return [...catalogItems, ...customItems];
  }, [selectedProducts, ProductObjs, customproducts]);

  // Catalog + custom line qty — ShippingOptions uses this for UPS eligibility (canCallRates).
  const totalQtyForShipping = React.useMemo(() => {
    const catalogQty = (selectedProducts || []).reduce((n, r) => n + (Number(r.quantity) || 0), 0);
    const customQty = (customproducts || []).reduce((n, cp) => n + (Number(cp.quantity) || 0), 0);
    return catalogQty + customQty;
  }, [selectedProducts, customproducts]);

  /** Picker: batch restore map + lines already on the order so Studio state survives reopening the modal. */
  const mergedStudioBatchVariantPreviewsForPicker = React.useMemo(() => {
    const fromCart = studioBatchVariantPreviewsFromCartLines(selectedProducts || []);
    const fromBatch = studioBatchVariantPreviews;
    if (!fromBatch || Object.keys(fromBatch).length === 0) return fromCart;
    return { ...fromBatch, ...fromCart };
  }, [selectedProducts, studioBatchVariantPreviews]);

  // Latest formik values for Studio create-context — `createOrderStudioCustomization` is memoized on
  // `studioReturnBatchId` only; without this ref, `getOrderSnapshot` would close over a stale `formik`
  // and 2nd+ customization (with batch_id) could omit buyer_type / customer_* fields.
  const formikValuesForStudioSnapshotRef = React.useRef(formik.values);
  formikValuesForStudioSnapshotRef.current = formik.values;

  const createOrderStudioCustomization = React.useMemo(
    (): StudioCustomizationFlowProps => ({
      source: "signage_create_order",
      existingBatchId: studioReturnBatchId.trim() || undefined,
      getOrderSnapshot: async () => {
        const built = await buildOrderPayloadForApiRef.current?.(formikValuesForStudioSnapshotRef.current);
        return (built ?? {}) as Record<string, unknown>;
      },
    }),
    [studioReturnBatchId]
  );

  // --- 4) Memo: can we proceed to "Save"? ---
  const canProceedToSave = React.useMemo(() => {
    const hasRequiredContactInfo =
      Boolean(String(formik.values.userEmail || "").trim()) &&
      Boolean(String(formik.values.userPhone || "").trim());
    const hasShippingAddress = !!shippingAddressForRates;

    // 2. Shipping option is required (either manual or UPS)
    const shippingOptions = shipPayloadState?.shipping_option || [];
    const hasShippingOption = shippingOptions.length > 0;

    // 3. Special fee: check if any required fees exist and are selected
    const specialFees = feePayloadState?.specialfee || [];
    const selectedFeeIds = feePayloadState?.selected_fee_ids || [];

    // If there are required fees, they must be selected
    const requiredFees = specialFees.filter(f => (f as any).required === true);
    const hasRequiredFees = requiredFees.length > 0;
    const allRequiredFeesSelected = hasRequiredFees
      ? requiredFees.every(rf => selectedFeeIds.includes((rf as any).specialfee_id))
      : true; // No required fees means validation passes

    return hasRequiredContactInfo && hasShippingAddress && hasShippingOption && allRequiredFeesSelected;
  }, [formik.values.userEmail, formik.values.userPhone, shippingAddressForRates, shipPayloadState, feePayloadState, effectiveCouponCreatedById]);



  // ✅ REMOVED: pullChildPayloads hooks - child components now emit via onPayloadChange
  // Child components (ShippingOptions, SpecialFee) now handle their own state and emit
  // changes via onPayloadChange callbacks. No need to pull from refs.


  const handleShipPayloadChange = React.useCallback((p: ShipPayload) => {
    const incomingRows = Array.isArray(p?.shipping_option) ? p.shipping_option : [];
    const newIds = shippingOptionIdsKey(incomingRows);
    const prevIds = shippingOptionIdsKey(shipPayloadRef.current?.shipping_option ?? []);
    if (newIds && newIds !== prevIds) {
      userEditedShippingAmountRef.current = false;
    }

    const normalizedRows = userEditedShippingAmountRef.current
      ? applyUserLockedShippingAmounts(
          incomingRows,
          shipPayloadRef.current?.shipping_option
        )
      : incomingRows;
    const nextPayload: ShipPayload = {
      ...p,
      shipping_option: normalizedRows,
    };

    shipPayloadRef.current = nextPayload;
    setShipPayloadState(prev => {
      const prevKey = JSON.stringify({
        rows: (prev?.shipping_option ?? []).map(r => [r.shipping__id ?? r.id, r.shipping_amount, r.shipping_tax_amount]),
        ids: prev?.selected_ids, ups: !!prev?.isUps, man: !!prev?.isManual
      });
      const nextKey = JSON.stringify({
        rows: (nextPayload?.shipping_option ?? []).map(r => [r.shipping__id ?? r.id, r.shipping_amount, r.shipping_tax_amount]),
        ids: nextPayload?.selected_ids, ups: !!nextPayload?.isUps, man: !!nextPayload?.isManual
      });
      return (prevKey === nextKey) ? prev : nextPayload;
    });
    if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
    recomputeTimerRef.current = setTimeout(() => {
      void recomputeSalesRef.current?.({ shipPayload: nextPayload });
    }, 300);
  }, []);

  const handleFeePayloadChange = React.useCallback((p: FeePayload) => {
    feePayloadRef.current = p;
    setFeePayloadState(prev => {
      const prevKey = JSON.stringify({
        rows: (prev?.specialfee ?? []).map(f => [f.specialfee_id, f.specialfee_amount, f.specialfee_tax_amount]),
        ids: prev?.selected_fee_ids
      });
      const nextKey = JSON.stringify({
        rows: (p?.specialfee ?? []).map(f => [f.specialfee_id, f.specialfee_amount, f.specialfee_tax_amount]),
        ids: p?.selected_fee_ids
      });
      return (prevKey === nextKey) ? prev : p;
    });
    if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
    recomputeTimerRef.current = setTimeout(() => {
      void recomputeSalesRef.current?.({ feePayload: p });
    }, 300);
  }, []);
  React.useEffect(() => {
    setFeePayloadState(createEmptyFeePayload());
    setSpecialFeeAmount(0);
    setSpecialFeeTaxAmount(0);
    setBusyStates(prev => ({ ...prev, specialFee: false }));
  }, [effectiveCouponCreatedById, createEmptyFeePayload]);

  // Studio redirect (?batch_id=&context_id=): pipeline mirrors manual UX — batch → buyer search → SearchBAT (pick/office/team) → addresses → charge seeds + line customizations.
  React.useEffect(() => {
    const key = `${studioReturnBatchId}|${studioReturnContextId}`;
    if (!studioReturnBatchId || !studioReturnContextId || !token || !customerGroupId) return;
    if (restoredStudioReturnKeyRef.current === key) return;

    let cancelled = false;

    const mergeStudioContextsIntoRows = (contexts: ReturnType<typeof normalizeCustomizationBatchContexts>) => {
      setSelectedProducts((prev) => {
        if (!prev.length) return prev;
        let changed = false;
        const updated = prev.map((row) => {
          const ctx = contexts.find(
            (c) =>
              (studioReturnContextId && c.context_id === studioReturnContextId) ||
              c.line_item_key === row.variantId ||
              c.variant_id === row.variantId ||
              c.product_id === row.productId
          );
          if (!ctx || !ctx.design_id) return row;
          changed = true;
          return {
            ...row,
            studio_design_id: ctx.design_id,
            studio_preview_id: ctx.preview_id,
            studio_design_name: ctx.design_name,
            studio_context_id: ctx.context_id,
            design_id: ctx.design_id,
            design_name: String(ctx.design_name ?? ""),
            preview_url: String(ctx.preview_id ?? ""),
          };
        });
        return changed ? updated : prev;
      });
    };

    (async () => {
      studioRestoreGuardRef.current = true;
      try {
        const batch = await getCustomizationBatch(studioReturnBatchId);
        if (cancelled) return;

        const contexts = normalizeCustomizationBatchContexts(batch);
        const batchOrder = (batch as { order?: Record<string, any> }).order;

        mergeStudioContextsIntoRows(contexts);
        setStudioBatchVariantPreviews(studioBatchVariantPreviewsFromBatchItems(batch as { items?: unknown[] }));

        if (!batchOrder || typeof batchOrder !== "object") {
          restoredStudioReturnKeyRef.current = key;
          return;
        }

        const apiBase = process.env.REACT_APP_API_URL || "";

        // Step 1 — Buyer: same `created-or-linked-detailed` + row match as choosing a search result (brokerage → team → agent → name).
        const match = await findBuyerSearchMatchForStudioRestore(
          apiBase,
          token,
          customerGroupId,
          batchOrder,
          () => cancelled
        );
        if (cancelled) return;

        let bat = searchBatRestoreRef.current;
        if (match && !bat) {
          await new Promise((r) => window.setTimeout(r, 50));
          bat = searchBatRestoreRef.current;
        }

        // Step 2 — Interlinked UI: same ref hooks as Continue (pick → contact → office → team → their follow-up fetches).
        if (match && bat) {
          bat.restoreApplyPick(match);
          bat.restoreSyncContact(contactFromOrderSnapshot(batchOrder));
          await new Promise((r) => window.setTimeout(r, 0));

          const lt = effectiveLicenseTypeForItem(match);
          const officeIdRaw = batchOrder.office?._id ?? batchOrder.office?.id;
          const teamIdRaw = batchOrder.team_member_id;

          if (lt === "BROKERAGE_FIRM" || lt === "BROKERAGE_TEAM_MEMBER") {
            if (isMongoObjectId(officeIdRaw)) {
              await bat.restoreSelectOfficeById(String(officeIdRaw));
            }
            if (isMongoObjectId(teamIdRaw)) {
              await bat.restoreSelectTeamMemberById(String(teamIdRaw));
            }
          } else if (lt === "OFFICE" && isMongoObjectId(teamIdRaw)) {
            await bat.restoreSelectTeamMemberById(String(teamIdRaw));
          }
        }

        // Step 3 — Addresses: wait for list from normal `fetchAddresses` chain, then bind batch snapshot ids to those rows.
        const waitForAddressList = async () => {
          for (let i = 0; i < 100; i++) {
            if (cancelled) return [];
            const a = addressObjectsMirrorRef.current;
            if (Array.isArray(a) && a.length) return a;
            await new Promise((r) => window.setTimeout(r, 100));
          }
          return Array.isArray(addressObjectsMirrorRef.current) ? addressObjectsMirrorRef.current : [];
        };

        const addrList = match ? await waitForAddressList() : [];
        if (cancelled) return;

        const addrBind = studioRestoreAddressBindings(batchOrder, addrList);
        formik.setFieldValue("isSameAsBilling", addrBind.formik.isSameAsBilling);
        formik.setFieldValue("billingAddressId", addrBind.formik.billingAddressId);
        formik.setFieldValue("shippingAddressId", addrBind.formik.shippingAddressId);
        formik.setFieldValue("notes", addrBind.formik.notes);
        setNotes(addrBind.notes);

        if (addrBind.useOfficeBilling) {
          setSelectedOfficeBillingAddress(addrBind.billingAddressObj);
          setSelectedOfficeShippingAddress(addrBind.shippingAddressObj);
        } else {
          setSelectedBillingAddress(addrBind.billingAddressObj);
          setSelectedShippingAddress(addrBind.shippingAddressObj);
        }

        // Step 4 — Shipping / special fees: children fetch like normal; seeds only align batch `order_charges` to loaded options.
        const chargeSeeds = studioRestoreChargesSeedsFromOrder(batchOrder);
        setStudioRestoreShippingInitialIds(chargeSeeds.shippingInitialIds);
        setStudioRestoreShippingSavedRows(chargeSeeds.shippingSavedRows);
        setStudioRestoreSpecialFeeInitialIds(chargeSeeds.specialFeeInitialIds);

        mergeStudioContextsIntoRows(contexts);

        if (!cancelled) {
          restoredStudioReturnKeyRef.current = key;
        }
      } catch (err) {
        safeConsole.warn("Studio batch restore failed:", err);
      } finally {
        studioRestoreGuardRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore when URL + auth + customer group are ready only
  }, [studioReturnBatchId, studioReturnContextId, token, customerGroupId]);

  // Exit shipping-edit mode when user has no shipping option selected (edit is only allowed when one is selected)
  React.useEffect(() => {
    const hasShippingOption = (shipPayloadState?.shipping_option?.length ?? 0) > 0;
    if (!hasShippingOption) setIsEditingShipping(false);
  }, [shipPayloadState?.shipping_option?.length]);


  const money = (v: any) =>
    Number.isFinite(Number(v)) ? `$${Number(v).toFixed(2)}` : "TBD";
  useEffect(() => {
    if (baseRoute == "/signageteammember") {
      setBaseId(parentSignageId || "");
    } else {
      setBaseId(userId || "");
    }
  }, [baseRoute, parentSignageId]);
  // wherever selectedBillingAddress / values.shippingAddressId change:
  React.useEffect(() => {
    const hasBilling = !!selectedBillingAddress || !!selectedOfficeBillingAddress;
    const hasShipping = !!formik.values.shippingAddressId;
    const hasAtLeastOneProduct = (selectedProducts?.length ?? 0) > 0 || (customproducts?.length ?? 0) > 0;

    if (hasBilling || hasShipping && hasAtLeastOneProduct) {
      setFormError(null);
    }
  }, [selectedBillingAddress, selectedOfficeBillingAddress, formik.values.shippingAddressId, selectedProducts, customproducts]);
  // keep local preview in sync with formik
  React.useEffect(() => {
    setNotes(formik.values.notes || "");
  }, [formik.values.notes]);


  // ========================================================================
  // HELPER FUNCTIONS - Price Resolution
  // ========================================================================
  // Gets the unit price to use for a product variant
  // Priority order: 1) Manual price (user edited), 2) Effective price from API, 3) Original price
  const getUnitPriceToUse = useCallback((productId: string, variantId: string, originalUnitPrice: number) => {
    const key = `${productId}:${variantId}`;
    // Priority: manual > effective from API > original
    if (manualUnitPriceMap[key] !== undefined) {
      return Number(manualUnitPriceMap[key] || 0);
    }
    return Number(effectiveMap[key] ?? originalUnitPrice ?? 0);
  }, [manualUnitPriceMap, effectiveMap]);

  const getEffectiveUnitPrice = useCallback((productId: string, variantId: string, originalUnitPrice: number) => {
    const key = `${productId}:${variantId}`;
    return Number(effectiveMap[key] ?? originalUnitPrice ?? 0);
  }, [effectiveMap]);

  const getBaseUnitPrice = useCallback(
    (productId: string, variantId: string, originalUnitPrice: number) => {
      const key = `${productId}:${variantId}`;
      if (basePriceMap[key] !== undefined) return Number(basePriceMap[key]);
      const productObj = ProductObjs?.find((o) => o.product_id === productId);
      const catalogRetail = Number(productObj?.product?.retail_price || 0);
      if (catalogRetail > 0) return catalogRetail;
      if (manualUnitPriceMap[key] === undefined) return Number(originalUnitPrice ?? 0);
      return catalogRetail;
    },
    [basePriceMap, ProductObjs, manualUnitPriceMap]
  );

  // Case 1 (qty → assignment discount): actual base price. Case 2 (manual edit): user-entered price.
  const getUnitRetailPriceForPayload = useCallback(
    (productId: string, variantId: string, originalUnitPrice: number) => {
      const key = `${productId}:${variantId}`;
      if (manualUnitPriceMap[key] !== undefined) {
        return Number(manualUnitPriceMap[key] || 0);
      }
      return getBaseUnitPrice(productId, variantId, originalUnitPrice);
    },
    [manualUnitPriceMap, getBaseUnitPrice]
  );

  const getChangedUnitPriceForPayload = useCallback(
    (productId: string, variantId: string, originalUnitPrice: number) =>
      getUnitPriceToUse(productId, variantId, originalUnitPrice),
    [getUnitPriceToUse]
  );

  // Shared with form submit and "Customize in Studio" (customization context order snapshot).
  buildOrderPayloadForApiRef.current = async (values: any) => {
    return buildSalesOrderPayloadForApi(values, {
      token,
      ensureDraftUniqueId,
      shippingCosts,
      shipPayloadState,
      feePayloadState,
      selectedBillingAddress,
      selectedShippingAddress,
      productTotals,
      shippingTaxAmount,
      specialFeeTaxAmount,
      productTaxMap,
      perItemCouponMap,
      couponDiscounts: [],
      selectedBrokerageOption,
      selectedOfficeAgent,
      selectedAgent,
      selectedOffice,
      selectedTeamMemberOption,
      offices,
      selectedOfficeBillingAddress,
      selectedOfficeShippingAddress,
      addressObjects,
      projectPrefillAddresses: projectMessageAddressesRef.current,
      teamMemberFromContinueRef,
      customproducts,
      selectedProducts,
      ProductObjs,
      linkedProjectId,
      projectIdFromQuery,
      linkedProjectNumber,
      isSignageTeammember,
      parentSignageId,
      parentSignageName,
      userId,
      userName,
      selectedShippingMethod,
      getUnitPriceToUse,
      getEffectiveUnitPrice,
      getUnitRetailPriceForPayload,
    });
  };

  const applyResolvedSalesToUi = React.useCallback(
    (resolved: ResolvedSalesOrder, requestedDiscountCode?: string) => {
      const sync = applySalesResolveResponse(resolved, {
        requestedDiscountCode,
        userClearedCoupon: userClearedCouponRef.current,
        existingFeeRows: feePayloadRef.current?.specialfee,
        existingShipRows: shipPayloadRef.current?.shipping_option,
        specialFeeCatalog: specialFeeCatalogRef.current,
        shippingCostCatalog: shippingCosts,
        userEditedShippingAmount: userEditedShippingAmountRef.current,
      });
      setProductTaxMap(sync.productTaxMap);
      setProductTaxPctMap(sync.productTaxPctMap);
      setTaxBreakdown(sync.taxBreakdown);
      setProductTotals(sync.productTotals);
      setEffectiveMap((prev) => ({ ...prev, ...sync.effectiveMap }));
      setBasePriceMap((prev) => ({ ...prev, ...sync.basePriceMap }));
      if (sync.feePayloadPatch?.specialfee?.length) {
        const resolvedFees = sync.feePayloadPatch.specialfee;
        const nextFeePayload: FeePayload = {
          specialfee: resolvedFees,
          selected_fee_ids: (resolvedFees as Array<{ specialfee_id?: string }>).map((f) =>
            String(f.specialfee_id ?? "")
          ).filter(Boolean),
          total_amount: sync.feePayloadPatch.total_amount,
          total_tax_amount: sync.feePayloadPatch.total_tax_amount,
          total_tax_percentage: 0,
        };
        feePayloadRef.current = nextFeePayload;
        setFeePayloadState(nextFeePayload);
      }
      if (sync.shipPayloadPatch?.shipping_option?.length) {
        setShipPayloadState((prev) => {
          const existing =
            (Array.isArray(shipPayloadRef.current?.shipping_option) &&
            shipPayloadRef.current.shipping_option.length
              ? shipPayloadRef.current.shipping_option
              : Array.isArray(prev?.shipping_option) && prev.shipping_option.length
                ? prev.shipping_option
                : []) ?? [];
          const merged = mergeResolvedShippingTaxIntoLocal(
            existing,
            sync.shipPayloadPatch!.shipping_option as Record<string, unknown>[]
          );
          const next = {
            ...(prev ?? shipPayloadRef.current ?? {}),
            shipping_option: merged,
          };
          shipPayloadRef.current = next;
          return next;
        });
      }
      setSelectedProducts((prev) =>
        mergeSelectedProductsFromResolved(prev, resolved, manualUnitPriceMap)
      );
      setLiveDiscountDisplay(sync.discountDisplay);
      setResolvedTotalsTick((t) => t + 1);
    },
    [manualUnitPriceMap, shippingCosts]
  );

  const recomputeSalesOrder = React.useCallback(
    async (override?: {
      discountCode?: string;
      force?: boolean;
      feePayload?: FeePayload;
      shipPayload?: ShipPayload;
    }) => {
      safeConsole.log("[SIGNAGE:CREATE_ORDER:RECOMPUTE] start", {
        force: override?.force ?? false,
        hasDiscountOverride: override?.discountCode !== undefined,
        hasShipPayload: !!override?.shipPayload,
        hasFeePayload: !!override?.feePayload,
      });

      if (!token) {
        safeConsole.warn("[SIGNAGE:CREATE_ORDER:RECOMPUTE] skipped: missing token");
        return false;
      }
      if (isRecomputingRef.current && !override?.force) {
        pendingRecomputeRef.current = true;
        safeConsole.log("[SIGNAGE:CREATE_ORDER:RECOMPUTE] deferred: already recomputing (non-force)");
        return false;
      }

      const requestId = ++recomputeRequestIdRef.current;

      const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;
      const buyer = pickSalesResolveBuyerContext({
        values: formikValuesRef.current as any,
        selectedOfficeAgent,
        selectedAgent,
        selectedOffice,
        offices,
        selectedBrokerageFirm: selectedBrokerageOption,
        selectedTeamMemberOption,
        selectedBillingAddress,
        selectedShippingAddress,
        selectedOfficeBillingAddress,
        selectedOfficeShippingAddress,
      });
      if (!signageId || !buyer.customerId || !(selectedProducts?.length || customproducts?.length)) {
        safeConsole.warn("[SIGNAGE:CREATE_ORDER:RECOMPUTE] skipped: missing required context", {
          signageId: signageId || null,
          customerId: buyer.customerId || null,
          selectedProductsCount: selectedProducts?.length ?? 0,
          customProductsCount: customproducts?.length ?? 0,
          buyerType: buyer.buyerType,
        });
        return false;
      }

      const discountCode = getSalesResolveDiscountCode({
        override: override?.discountCode,
        userClearedCoupon: userClearedCouponRef.current,
        couponInput,
        liveDiscountCode: liveDiscountDisplay.code,
      });

      isRecomputingRef.current = true;
      setIsRecomputingSales(true);
      setCouponResolveError("");

      try {
        const cartId = await ensureDraftUniqueId(token, null);
        const payload = buildSalesResolvePayload({
          signageId: String(signageId),
          signageCompanyName: (isSignageTeammember && parentSignageName) ? parentSignageName! : userName!,
          buyerType: buyer.buyerType,
          customerId: buyer.customerId,
          agentId: buyer.agentId,
          agentName: buyer.agentName,
          brokerageFirmId: buyer.brokerageFirmId,
          brokerageFirmName: buyer.brokerageFirmName,
          office: buyer.office,
          teamMemberId: buyer.teamMemberId,
          teamMemberName: buyer.teamMemberName,
          customerFirstName: buyer.customerFirstName,
          customerLastName: buyer.customerLastName,
          customerEmail: buyer.customerEmail,
          customerPhone: buyer.customerPhone,
          billingAddress: buyer.billingAddress,
          shippingAddress: buyer.shippingAddress,
          bothAddressSame: !!formikValuesRef.current.isSameAsBilling,
          inventorySource: buyer.inventorySource,
          discountCode,
          cartId,
          selectedProducts,
          customproducts,
          productObjs: ProductObjs,
          shipPayload: override?.shipPayload ?? shipPayloadRef.current ?? shipPayloadState,
          feePayload: override?.feePayload ?? feePayloadRef.current ?? feePayloadState,
          shippingCosts,
          specialFeeCatalog: specialFeeCatalogRef.current,
          manualUnitPriceMap,
          getUnitRetailPriceForPayload,
          getChangedUnitPriceForPayload,
        });

        const resolved = await postSalesOrderResolve(token, {
          order_type: "order",
          action: "draft",
          // action: "",
          payload,
        });

        if (requestId !== recomputeRequestIdRef.current) {
          safeConsole.log("[SIGNAGE:CREATE_ORDER:RECOMPUTE] superseded by newer request", {
            requestId,
            currentRequestId: recomputeRequestIdRef.current,
          });
          return true;
        }

        lastResolvedSalesRef.current = resolved;
        applyResolvedSalesToUi(resolved, discountCode);
        safeConsole.log("[SIGNAGE:CREATE_ORDER:RECOMPUTE] success", {
          requestId,
          orderNumber: resolved?.order_number,
          totalAmount: resolved?.total_amount,
          discountCode,
        });
        return true;
      } catch (err: any) {
        if (requestId !== recomputeRequestIdRef.current) {
          safeConsole.log("[SIGNAGE:CREATE_ORDER:RECOMPUTE] error ignored (superseded request)", {
            requestId,
            currentRequestId: recomputeRequestIdRef.current,
          });
          return false;
        }
        const msg = String(err?.message ?? "Failed to recalculate order");
        safeConsole.error("[SIGNAGE:CREATE_ORDER:RECOMPUTE] failed", { requestId, message: msg, err });
        setCouponResolveError(msg);
        showError(msg);
        return false;
      } finally {
        if (requestId === recomputeRequestIdRef.current) {
          isRecomputingRef.current = false;
          setIsRecomputingSales(false);
          if (pendingRecomputeRef.current) {
            pendingRecomputeRef.current = false;
            void recomputeSalesRef.current?.({ force: true });
          }
        }
      }
    },
    [
      token,
      isSignageTeammember,
      parentSignageId,
      userId,
      parentSignageName,
      userName,
      selectedOfficeAgent,
      selectedAgent,
      selectedOffice,
      offices,
      selectedBrokerageOption,
      selectedBillingAddress,
      selectedShippingAddress,
      selectedOfficeBillingAddress,
      selectedOfficeShippingAddress,
      couponInput,
      liveDiscountDisplay.code,
      selectedProducts,
      customproducts,
      ProductObjs,
      shipPayloadState,
      feePayloadState,
      shippingCosts,
      manualUnitPriceMap,
      getUnitRetailPriceForPayload,
      getChangedUnitPriceForPayload,
      applyResolvedSalesToUi,
    ]
  );

  const scheduleSalesRecompute = React.useCallback(
    (override?: {
      discountCode?: string;
      force?: boolean;
      feePayload?: FeePayload;
      shipPayload?: ShipPayload;
    }) => {
      if (isRecomputingRef.current && !override?.force) return;
      if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
      recomputeTimerRef.current = setTimeout(() => {
        void recomputeSalesOrder(override);
      }, 300);
    },
    [recomputeSalesOrder]
  );

  scheduleSalesRecomputeRef.current = scheduleSalesRecompute;
  recomputeSalesRef.current = recomputeSalesOrder;

  const shippingAddressSig = React.useMemo(() => {
    const a = selectedShippingAddress as { address_details?: Record<string, unknown> } | null;
    const d = a?.address_details;
    if (!d) return "";
    return [d.addressLine1, d.addressLine2, d.city, d.stateCode ?? d.state, d.zip]
      .map((x) => String(x ?? "").trim())
      .join("|");
  }, [selectedShippingAddress]);

  React.useEffect(() => {
    if (!shippingAddressSig) return;
    const hasLineItems =
      (selectedProducts || []).some((p) => p.productId && p.variantId) ||
      (customproducts || []).length > 0;
    if (!hasLineItems) return;
    scheduleSalesRecomputeRef.current?.();
  }, [
    shippingAddressSig,
    selectedProducts?.length,
    customproducts?.length,
    (customproducts || [])
      .map((cp) => `${cp.product_id}:${cp.variant_id}:${cp.quantity}:${cp.Price}:${cp.is_taxable}`)
      .join("|"),
  ]);

  // ✅ Stable key to prevent running on every object reference change
  const shipPayloadKey = useMemo(() =>
    JSON.stringify(shipPayloadState?.shipping_option?.map((r: any) => [r.shipping__id, r.shipping_amount, r.shipping_tax_amount])),
    [shipPayloadState?.shipping_option]
  );

  const feePayloadKey = useMemo(() =>
    JSON.stringify(feePayloadState?.specialfee?.map((f: any) => [f.specialfee_id, f.specialfee_amount, f.specialfee_tax_amount])),
    [feePayloadState?.specialfee]
  );

  // 🔄 whenever ShippingOptions payload changes, update amounts in parent
  useEffect(() => {
    const rows = Array.isArray(shipPayloadState?.shipping_option)
      ? shipPayloadState.shipping_option
      : [];

    const amt = rows.reduce((s: number, r: any) => s + Number(r?.shipping_amount || 0), 0);
    const tax = rows.reduce((s: number, r: any) => s + Number(r?.shipping_tax_amount || 0), 0);

    setShippingAmount(amt);
    setShippingTaxAmount(tax);
    // console.log("[ORDER:SHIPPING_TOTALS] amount:", amt, "tax:", tax);
  }, [shipPayloadKey]); // ✅ Use stable key instead of object

  // 🔄 whenever SpecialFee payload changes, update amounts in parent
  useEffect(() => {
    const rows = Array.isArray(feePayloadState?.specialfee)
      ? feePayloadState.specialfee
      : [];
    // console.log("Special Fee Rows:", rows);
    const amt = rows.reduce((s: number, r: any) => s + Number(r?.specialfee_amount || 0), 0);
    const tax = rows.reduce((s: number, r: any) => s + Number(r?.specialfee_tax_amount || 0), 0);

    setSpecialFeeAmount(amt);
    setSpecialFeeTaxAmount(tax);
    // console.log("[ORDER:SPECIAL_FEE_TOTALS] amount:", amt, "tax:", tax);
  }, [feePayloadKey]); // ✅ Use stable key instead of object

  const subTotalProducts = Number(productTotals.subTotal || 0);
  const otherCharges = Number(shippingAmount || 0) + Number(specialFeeAmount || 0);

  // Custom items tax from productTaxMap (keys productId:variantId); ensure totalTaxAll includes it
  const customProductsTaxSum = React.useMemo(() => {
    const round2 = (n: number) => Math.round(n * 100) / 100;
    return round2(
      (customproducts || []).reduce(
        (sum, p) => sum + Number(productTaxMap[keyForRow({ productId: p.product_id, variantId: p.variant_id })] ?? 0),
        0
      )
    );
  }, [customproducts, productTaxMap]);

  const totalTaxAll = React.useMemo(() => {
    const fromResolved = lastResolvedSalesRef.current?.order_tax_summary?.over_all_tax_amount;
    if (fromResolved != null && Number.isFinite(Number(fromResolved))) {
      return Number(fromResolved);
    }
    const fromBreakdown =
      Number(taxBreakdown.product || 0) +
      Number(taxBreakdown.shipping || 0) +
      Number(taxBreakdown.specialFee || 0);
    if (fromBreakdown > 0) return fromBreakdown;
    return (
      Number(productTotals.productTax || 0) +
      Number(shippingTaxAmount || 0) +
      Number(specialFeeTaxAmount || 0) +
      customProductsTaxSum
    );
  }, [
    productTotals.productTax,
    shippingTaxAmount,
    specialFeeTaxAmount,
    customProductsTaxSum,
    taxBreakdown,
    resolvedTotalsTick,
  ]);
  const discountForGrandTotal = Number(liveDiscountDisplay.amount || 0);

  // Allow editing shipping charge only when user has selected a shipping option
  const canEditShipping = (shipPayloadState?.shipping_option?.length ?? 0) > 0;

  const shippingRowsForTotalsLabel = React.useMemo(() => {
    const rows = shipPayloadState?.shipping_option;
    return Array.isArray(rows) && rows.length > 0 ? rows : [];
  }, [shipPayloadState?.shipping_option]);

  const isUpsShippingForTotalsLabel = React.useMemo(() => {
    const rows = shipPayloadState?.shipping_option ?? [];
    if (!rows.length) return false;
    if (typeof shipPayloadState?.isUps === "boolean") return !!shipPayloadState.isUps;
    const r = rows[0] as Record<string, unknown>;
    const rawId = String(r?.shipping__id ?? r?.shipping_id ?? "");
    if (!rawId || rawId === "custom-manual") return false;
    if (/^[0-9A-Z]{2,3}$/.test(rawId) && rawId.length <= 3) return true;
    return /^ups$/i.test(String(r?.shipping_type ?? "").trim());
  }, [shipPayloadState?.shipping_option, shipPayloadState?.isUps]);

  const specialFeeRowsForTotalsLabel = React.useMemo(() => {
    const rows = feePayloadState?.specialfee;
    return Array.isArray(rows) && rows.length > 0 ? rows : [];
  }, [feePayloadState?.specialfee]);

  const subTotalProductsBeforeDiscount = React.useMemo(() => {
    const rows = Array.isArray(selectedProducts) ? selectedProducts : [];
    const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    const round2 = (n: number) => Math.round(num(n) * 100) / 100;

    return round2(
      rows.reduce((sum, r) => {
        // ✅ Use manual price if exists, otherwise effective from API, otherwise original
        const unitPriceToUse = getUnitPriceToUse(r.productId, r.variantId, num(r.unitPrice || 0));
        return sum + unitPriceToUse * Math.max(0, num(r.quantity));
      }, 0)
    );
  }, [selectedProducts, getUnitPriceToUse]);

  // Custom products subtotal (before discount; no discount on custom items)
  const customProductsSubtotal = React.useMemo(() => {
    const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);
    const round2 = (n: number) => Math.round(num(n) * 100) / 100;
    return round2(
      (customproducts || []).reduce(
        (sum, cp) => sum + num(cp.Price) * Math.max(0, num(cp.quantity)),
        0
      )
    );
  }, [customproducts]);

  // Sub total shown in Order Totals = catalog + custom (before discount)
  const subTotalAllBeforeDiscount = subTotalProductsBeforeDiscount + customProductsSubtotal;

  const grandTotal = React.useMemo(() => {
    const round2 = (n: number) => Math.round(Number(n) * 100) / 100;
    return round2(
      subTotalAllBeforeDiscount + otherCharges + totalTaxAll - discountForGrandTotal
    );
  }, [
    subTotalAllBeforeDiscount,
    otherCharges,
    totalTaxAll,
    discountForGrandTotal,
    resolvedTotalsTick,
  ]);
  const approvedProofCards = React.useMemo(() => {
    const approvedRoot = (approvedMessageData as any) || {};
    const approvedFiles = Array.isArray(approvedRoot?.approved_files) ? approvedRoot.approved_files : [];
    if (approvedFiles.length === 0) return [];

    const grouped = new Map<number, any[]>();
    approvedFiles.forEach((f: any) => {
      const version = Number(f?.version);
      const key = Number.isFinite(version) && version > 0 ? Math.floor(version) : 1;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(f);
    });

    return Array.from(grouped.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([version, files]) => {
        const first = files[0] || {};
        const compactProofFiles = files.map((f: any, idx: number) => ({
          id: String(f?.id ?? f?._id ?? idx),
          name: String(f?.original_name ?? f?.file_name ?? f?.name ?? "File"),
          href: String(f?.preview_url ?? "").trim() || String(f?.file_url ?? f?.url ?? "").trim(),
          previewUrl: String(f?.preview_url ?? f?.file_url ?? f?.url ?? "").trim(),
          fileId: String(f?.file_id ?? "").trim(),
          quantity: Number.isFinite(Number(f?.quantity)) ? Number(f?.quantity) : undefined,
          sizeLabel: Number.isFinite(Number(f?.file_size)) ? `${Math.round(Number(f?.file_size) / 1024)} KB` : "—",
        }));
        const approvedByName = String(
          first?.design_approved_by?.name ??
          first?.updated_by?.name ??
          approvedRoot?.design_approved_by?.name ??
          approvedRoot?.approved_by_name ??
          ""
        ).trim();
        const approvedAtRaw =
          first?.design_approved_at ??
          first?.updated_at ??
          approvedRoot?.design_approved_at ??
          approvedRoot?.approved_at;
        const approvedAtLabel = (() => {
          if (!approvedAtRaw) return "";
          const dt = new Date(approvedAtRaw);
          return Number.isNaN(dt.getTime()) ? "" : dt.toLocaleString();
        })();
        const shippingName = String(first?.shipping_method?.name ?? approvedRoot?.shipping_method?.name ?? "").trim();
        const shippingDesc = String(first?.shipping_method?.description ?? approvedRoot?.shipping_method?.description ?? "").trim();
        const shippingAmountRaw = first?.shipping_amount ?? approvedRoot?.shipping_amount;
        const shippingAmountLabel = Number.isFinite(Number(shippingAmountRaw))
          ? `$${Number(shippingAmountRaw).toFixed(2)}`
          : "";
        const assignedToName = String(approvedRoot?.assigned_to_name ?? "").trim();
        const orderSummary = resolveApprovedProofVersionSummary(approvedRoot, version, first);
        return {
          version,
          files: compactProofFiles,
          approvedByName,
          approvedAtLabel,
          assignedToName,
          shippingName,
          shippingDesc,
          shippingAmountLabel,
          orderSummary,
        };
      });
  }, [approvedMessageData]);

  // ✅ Debug log with stable key to prevent logging on every render
  const totalsDebugKey = useMemo(() => JSON.stringify({
    subTotalProducts,
    shippingAmount,
    specialFeeAmount,
    productTax: productTotals.productTax,
    shippingTaxAmount,
    specialFeeTaxAmount,
    totalTaxAll,
    grandTotal,
  }), [
    subTotalProducts,
    shippingAmount,
    specialFeeAmount,
    productTotals.productTax,
    shippingTaxAmount,
    specialFeeTaxAmount,
    totalTaxAll,
    grandTotal,
  ]);

  const lastTotalsDebugRef = React.useRef("");
  useEffect(() => {
    if (lastTotalsDebugRef.current === totalsDebugKey) return;
    lastTotalsDebugRef.current = totalsDebugKey;
    // console.log("[ORDER:TOTALS] recomputed", JSON.parse(totalsDebugKey));
  }, [totalsDebugKey]);

  // ✅ Handle edit shipping amount
  const handleEditShipping = () => {
    setManualShippingAmount(shippingAmount.toString());
    setIsEditingShipping(true);
  };

  // ✅ Handle cancel edit
  const handleCancelEditShipping = () => {
    setIsEditingShipping(false);
    setManualShippingAmount("");
  };

  // ✅ Handle save edited shipping amount: keep user-selected option and apply custom amount so payload has both
  const handleSaveShipping = () => {
    const newAmount = Number(manualShippingAmount);

    if (isNaN(newAmount) || newAmount < 0) {
      showError("Please enter a valid shipping amount");
      return;
    }

    const currentRows = shipPayloadState?.shipping_option ?? [];

    if (currentRows.length > 0) {
      // Keep user-selected shipping option(s) and override amount with custom value so submit passes both
      const updatedRows = currentRows.map((row: any, i: number) =>
        i === 0
          ? {
            ...row,
            shipping_amount: newAmount,
            cost: newAmount,
          }
          : row
      );
      const newPayload: ShipPayload = {
        ...shipPayloadRef.current,
        shipping_option: updatedRows,
      };
      shipPayloadRef.current = newPayload;
      setShipPayloadState(newPayload);
      userEditedShippingAmountRef.current = true;
      void recomputeSalesRef.current?.({ shipPayload: newPayload });
      showSuccess("Shipping amount updated.");
    } else {
      // No selection yet: create custom manual entry only
      const firstRow = currentRows[0];
      const pickedCost = shippingCosts.find(
        (c) => String(c._id) === String(firstRow?.shipping__id ?? firstRow?.shipping_id ?? "")
      );
      const manualTaxable = readResolveTaxable(
        firstRow as Record<string, unknown> | undefined,
        ["shipping_taxable", "taxable", "is_taxable"],
        readResolveTaxable(
          pickedCost as Record<string, unknown> | undefined,
          ["taxable", "is_taxable", "shipping_taxable"],
          true
        )
      );
      const customShippingEntry = {
        shipping__id: "custom-manual",
        shipping_id: "custom-manual",
        shipping_name: "Custom Shipping (User Edited)",
        shipping_type: "MANUAL",
        is_customer_pickup: false,
        shipping_amount: newAmount,
        cost: newAmount,
        shipping_tax_percentage: 0,
        shipping_tax_amount: 0,
        shipping_taxable: manualTaxable,
        shipping_delivarydate: "",
        shipping_delivery_time: "",
      };
      const newPayload: ShipPayload = {
        shipping_option: [customShippingEntry],
        isManual: true,
        isUps: false,
        selected_ids: {
          manual_shipping_id: null,
          ups_service_code: null,
          integration_id: null,
          shipper_address_id: null,
        },
      };
      shipPayloadRef.current = newPayload;
      setShipPayloadState(newPayload);
      userEditedShippingAmountRef.current = true;
      void recomputeSalesRef.current?.({ shipPayload: newPayload });
      showSuccess("Shipping amount updated.");
    }

    setIsEditingShipping(false);
    setManualShippingAmount("");
  };


  // const money = (n: any) => `$${Number(n || 0).toFixed(2)}`;


  return (
    <div className="teammember container-fluid container-1440">
      <div className="">
        <h3 className="m-0 ms-2 fw-bold mb-3">Create Order</h3>
      </div>
      <div className="row ">
        {/* <div className="col-md-12 d-flex justify-content-center"> */}
        <div className="col-xl-8" >
          <div className=" p-1">
            <div className="">
              <form
                onSubmit={(e) => {
                  const submitter = (e.nativeEvent as SubmitEvent).submitter;
                  const isSaveButton = submitter?.getAttribute("data-save-button") === "true";
                  if (!isSaveButton) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  formik.handleSubmit(e);
                }}
              >
                <div className="">
                  <div className="box-shadow shadow-border-3 px-3 mb-5">
                    <div className="">
                      <div className="row ms-2">
                        <div id="row-overlap">
                          <h6 className="mb-0 d-flex align-items-center order-tittle ms-0 mt-3 mb-3">
                            <AppIcon name="orderDetails" size={22} alt="Order Details" className="me-2" />
                            <span>Order Details</span>
                            {orderTypeBadge && (
                              <span className={orderTypeBadge.cls} id="search-bat-type-badge" style={{ marginLeft: "8px" }}>
                                {orderTypeBadge.label.toUpperCase()}
                              </span>
                            )}
                          </h6>
                        </div>
                        <div className="col-12 mb-4" id="row-overlap" >
                          <div>
                          <SearchBrokerageAgentTeam
                            ref={searchBatRestoreRef}
                            customerGroupId={customerGroupId || ""}
                            token={token || ""}
                            readOnly={Boolean(String(projectIdFromQuery || "").trim())}
                            title="Create Order"
                            subtitle="Search for a brokerage, team member, or agent to begin"
                            initialLabel={projectContactPrefill?.label}
                            initialType={projectContactPrefill?.type}
                            initialContact={projectContactPrefill?.contact}
                            emailError={
                              formik.submitCount > 0 || formik.touched.userEmail
                                ? (formik.errors.userEmail as string) || ""
                                : ""
                            }
                            phoneError={
                              formik.submitCount > 0 || formik.touched.userPhone
                                ? (formik.errors.userPhone as string) || ""
                                : ""
                            }
                            onContactChange={(c: { fName: string; lName: string; email: string; phone: string }) => {
                              formik.setFieldValue("userFirstName", c?.fName ?? "");
                              formik.setFieldValue("userLastName", c?.lName ?? "");
                              formik.setFieldValue("userEmail", c?.email ?? "");
                              formik.setFieldValue("userPhone", withDefaultSignageContactPhone(c?.phone));
                            }}
                            onOfficeChange={(office: { id: string; label: string; company_name?: string; office_count?: number }) => {
                              applyStudioOfficeSelection(office);
                            }}
                            onTeamMemberChange={(member: {
                              id: string;
                              label: string;
                              firstName: string;
                              lastName: string;
                              email: string;
                              phone: string;
                            }) => {
                              applyStudioTeamMemberSelection(member);
                            }}
                            onAgentChange={(agent: {
                              id: string;
                              label: string;
                              firstName: string;
                              lastName: string;
                              email: string;
                              phone: string;
                            } | null) => {
                              if (!agent?.id) {
                                setSelectedOfficeAgent(null);
                                formik.setFieldValue("officeAgentSelection", "");
                                formik.setFieldValue("agentSelection", "");

                                const tm = selectedTeamMemberOption as {
                                  value?: string;
                                  firstName?: string;
                                  lastName?: string;
                                  email?: string;
                                  phone?: string;
                                } | null;
                                if (tm?.value) {
                                  formik.setFieldValue("userFirstName", tm.firstName || "");
                                  formik.setFieldValue("userLastName", tm.lastName || "");
                                  formik.setFieldValue("userEmail", tm.email || "");
                                  formik.setFieldValue(
                                    "userPhone",
                                    withDefaultSignageContactPhone(tm.phone)
                                  );
                                }

                                resetBuyerAddressSelections();

                                const addrUser =
                                  selectedOffice?.value
                                    ? { value: selectedOffice.value, label: selectedOffice.label }
                                    : selectedBrokerageOption
                                      ? {
                                          value: (selectedBrokerageOption as any).value,
                                          label: (selectedBrokerageOption as any).label,
                                        }
                                      : null;
                                if (addrUser) {
                                  void fetchAddresses(addrUser);
                                }
                                return;
                              }

                              const selected = {
                                value: String(agent.id),
                                label: agent.label || [agent.firstName, agent.lastName].filter(Boolean).join(" ").trim(),
                                firstName: agent.firstName || "",
                                lastName: agent.lastName || "",
                                email: agent.email || "",
                                phone: withDefaultSignageContactPhone(agent.phone),
                              };
                              setSelectedOfficeAgent(selected);
                              setSelectedAgent(null);
                              formik.setFieldValue("officeAgentSelection", selected.value);
                              formik.setFieldValue("agentSelection", selected.value);
                              formik.setFieldValue("userFirstName", selected.firstName);
                              formik.setFieldValue("userLastName", selected.lastName);
                              formik.setFieldValue("userEmail", selected.email);
                              formik.setFieldValue("userPhone", selected.phone);

                              resetBuyerAddressSelections();
                              void fetchAddresses(selected);
                            }}
                            onContinue={applyBuyerSearchContinuePayload}
                            onReset={() => {
                              teamMemberFromContinueRef.current = null;
                              formik.setFieldValue("userType", "");
                              resetBelow("userType");
                              setSelectedBrokerageOption(null);
                              setSelectedTeamMemberOption(null);
                              setSelectedAgent(null);
                              setBuyerType("");
                              // Reset product details (catalog + custom line items)
                              setSelectedProducts([]);
                              setCustomproducts([]);
                              // Reset discount (coupon) and shipping when clearing search
                              setCouponInput("");
                              setLiveDiscountDisplay({ code: "", amount: 0 });
                              userClearedCouponRef.current = true;
                              setPerItemCouponMap({});
                              const emptyShipPayload: ShipPayload = {
                                shipping_option: [],
                                isManual: false,
                                isUps: false,
                              };
                              shipPayloadRef.current = emptyShipPayload;
                              setShipPayloadState(emptyShipPayload);
                              setStudioRestoreShippingInitialIds(null);
                              setStudioRestoreShippingSavedRows(null);
                              setStudioRestoreSpecialFeeInitialIds(null);
                              setStudioBatchVariantPreviews(null);
                              // Remount ShippingOptions so their internal state (selection) clears
                              setOrderFormResetKey((k) => k + 1);
                            }}
                          />
                          </div>
                        </div>

                      </div>


                    </div>
                    <hr className="mt-3" />
                    <div className="row mb-2 mt-2">
                      <div className="my-2" id="row-overlap">
                        <h5 className="ms-3 table-headers">Billing Information</h5>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="form-check mb-0 ms-3">
                            <input
                              type="checkbox"
                              id="isSameAsBilling"
                              name="isSameAsBilling"
                              className="form-check-input me-2"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              checked={formik.values.isSameAsBilling}
                            />
                            <label className="form-check-label" htmlFor="isSameAsBilling">
                              Is Billing and Shipping address the same
                            </label>
                          </div>

                          <button
                            type="button"
                            className="btn btn-primary table-addbutton br-8"
                            onClick={() => setShowAddressForm(true)}
                          // onClick={() => setShowAddressModal(true)}
                          // disabled={!!selectedBillingAddress || !!selectedOfficeBillingAddress}
                          >
                            + Address
                          </button>
                        </div>
                      </div>

                      <div className="col-md-6" id="row-overlap">
                        {/* <h5 className="ms-3 table-headers">Address Details </h5> */}
                        <div className="my-2 ms-3">
                          <div className="">
                            <label className="form-label" htmlFor="billingAddress">
                              Select Billing Address <span className="text-danger">*</span>
                            </label>
                            <Select
                              key={`billing-address-${addressSelectKey}`}
                              id="billingAddress"
                              name="billingAddress"
                              options={addressOptions}
                              isClearable
                              placeholder="- Select -"
                              value={
                                addressOptions?.find(
                                  (option) => option.value === formik.values.billingAddressId
                                ) || null
                              }
                              onChange={(selected: any) => {
                                // console.log("Selected Billing Address:", selected);

                                // ✅ Set billingAddressId to value (NOT _id)
                                formik.setFieldValue("billingAddressId", selected?.value);

                                const selectedAddress = addressObjects?.find((addr) => addr._id === selected.value);
                                setSelectedOfficeBillingAddress(selectedAddress || null);
                                setSelectedBillingAddress(selectedAddress || null);
                              }}
                              onBlur={() => formik.setFieldTouched("billingAddressId", true)}
                              components={{ DropdownIndicator }}
                              styles={customSelectStyles}
                              classNamePrefix="rs"
                              menuPlacement="bottom"
                            />
                            {formik.touched.billingAddressId && formik.errors.billingAddressId && (
                              <div className="text-danger mt-1">
                                {formik.errors.billingAddressId}
                              </div>
                            )}
                          </div>

                        </div>
                      </div>
                      {!formik.values.isSameAsBilling && (
                        <div className="col-md-6 mt-3 mb-2" id="row-overlap">
                          <label className="form-label mt-2" htmlFor="shippingAddressId">
                            Select Shipping Address <span className="text-danger">*</span>
                          </label>
                          <Select
                            key={`shipping-address-${addressSelectKey}`}
                            id="shippingAddressId"
                            name="shippingAddressId"
                            options={addressOptions}
                            isClearable
                            placeholder="- Select -"
                            value={
                              addressOptions?.find(
                                (option) => option.value === formik.values.shippingAddressId
                              ) || null
                            }
                            onChange={(selected: any) => {
                              formik.setFieldValue("shippingAddressId", selected?.value);
                              const selectedAddress = addressObjects?.find((addr) => addr._id === selected.value);
                              setSelectedOfficeShippingAddress(selectedAddress || null);
                              setSelectedShippingAddress(selectedAddress || null);
                            }}
                            onBlur={() => formik.setFieldTouched("shippingAddressId", true)}
                            components={{ DropdownIndicator }}
                            styles={customSelectStyles}
                            classNamePrefix="rs"
                            menuPlacement="bottom"
                          />
                          {formik.touched.shippingAddressId && formik.errors.shippingAddressId && (
                            <div className="text-danger mt-1">
                              {formik.errors.shippingAddressId}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <hr className="mt-3" />
                    <>
                      {/* HEADER: left title + right button (stack on mobile) */}
                      <div className="card-header bg-white rounded-top border-0">
                          <div className="align-items-md-center align-items-start d-lg-flex flex-column flex-md-row gap-2 justify-content-between">
                            <h6 className="align-items-center d-flex mb-0 mb-3 mb-lg-0 ms-1 order-tittle">
                              <AppIcon name="productBox" size={22} alt="Product Details" />
                              <span className="ms-2">Product Details</span>
                            </h6>

                            {canShowAdd && (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary table-addbutton br-8 ms-md-auto me-2"
                                onClick={() => setPickerOpen(true)}
                              >
                                Add Product
                              </button>
                            )}
                            {canShowAdd && customProductSettingEnabled && (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary table-addbutton br-8"
                                onClick={() => setShowCustomModal(true)}
                              >
                                Custom Item
                              </button>
                            )}
                          </div>
                        </div>
                    <div
                      className="table-responsive mt-3 signage-create-order-products"
                      style={{
                        maxHeight: "300px",
                        overflowY: "auto",
                        overflowX: "auto",
                        border: "1px solid #dee2e6",
                        borderRadius: "0.25rem",
                        WebkitOverflowScrolling: "touch"
                      }}
                    >
                      <>
                     
                      <div style={{ maxHeight: "300px", overflowY: "auto", overflowX: "auto" }}>
                        <CustomItemModal
                          show={showCustomModal}
                          onClose={() => setShowCustomModal(false)}
                          onSubmit={async (data: any) => {
                            const t = token || "";
                            if (!t) {
                              showError("Session expired. Please log in again.");
                              return;
                            }
                            try {
                              const productId = await fetchNewUniqueId(t);
                              const variantId = await fetchNewUniqueId(t);
                              const qty = parseInt(String(data.quantity), 10) || 0;
                              const price = parseFloat(String(data.price)) || 0;
                              const taxable =
                                typeof data?.taxable === "boolean" ? data.taxable : true;
                              setCustomproducts((prev) => {
                                const next = [
                                  ...prev,
                                  {
                                    product_id: productId,
                                    variant_id: variantId,
                                    item_name: String(data.item_name || "").trim() || "Custom Item",
                                    quantity: qty,
                                    sku: "custom_sku",
                                    Price: price,
                                    is_taxable: taxable,
                                    item_tax_info: { taxable },
                                    custom_information: String(data.custom_info || "").trim(),
                                    length: parseFloat(String(data.length)) || 0,
                                    width: parseFloat(String(data.width)) || 0,
                                    height: parseFloat(String(data.height)) || 0,
                                    weight: parseFloat(String(data.weight)) || 0,
                                  },
                                ];
                                const idx = next.length - 1;
                                queueMicrotask(() => {
                                  const errorKey = purchasedProductErrorKey("custom", String(idx));
                                  patchPurchasedProductFieldError(
                                    errorKey,
                                    "quantity",
                                    validatePurchasedQuantityValue(qty)
                                  );
                                  patchPurchasedProductFieldError(
                                    errorKey,
                                    "unitPrice",
                                    validatePurchasedUnitPriceValue(price)
                                  );
                                });
                                return next;
                              });
                              setShowCustomModal(false);
                            } catch (err) {
                              showError(err instanceof Error ? err.message : "Failed to add custom item");
                              throw err;
                            }
                          }}
                        />

                        <table className="table mb-0 align-middle signage-create-order-products-table">
                          <colgroup>
                            <col className="sco-col-product" />
                            <col className="sco-col-sku" />
                            <col className="sco-col-qty" />
                            {/* <col className="sco-col-unit-price" />÷ */}
                            <col className="sco-col-changed-price" />
                            <col className="sco-col-discount" />
                            {/* <col className="sco-col-tax" /> */}
                            <col className="sco-col-total" />
                            <col className="sco-col-actions" />
                          </colgroup>
                          <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                            <tr>
                              <th className="text-center table-headers sco-th-product">Product</th>
                              <th className="text-center table-headers sco-th-sku">SKU</th>
                              <th className="text-center table-headers sco-th-qty">Qty</th>
                              <th className="text-center table-headers sco-th-unit-price" title="Unit Price">
                                Unit Price
                              </th>
                              {/* <th className="text-center table-headers sco-th-changed-price" title="Changed Price">
                                Changed<br />Price
                              </th> */}
                              <th className="text-center table-headers sco-th-discount">Discount</th>
                              {/* <th className="text-center table-headers sco-th-tax">Tax</th> */}
                              <th className="text-center table-headers sco-th-total">Total</th>
                              <th className="text-center table-headers sco-th-actions"></th>
                            </tr>
                          </thead>


                          <tbody>
                            {!hasRows ? (
                              canShowAdd ? (
                                <tr>
                                  {/* <td className="text-end" colSpan={colCount}>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-success px-3 br-8"
                                        onClick={() => setPickerOpen(true)}
                                      >
                                        Add Product
                                      </button>
                                    </td> */}
                                </tr>
                              ) : (
                                <tr>
                                  <td className="text-center text-muted" colSpan={colCount}>
                                    Select an agent/office then add products
                                  </td>
                                </tr>
                              )
                            ) : (
                              <>
                                {selectedProducts.map((product: any, index: number) => {
                                  const isLast = index === selectedProducts.length - 1;
                                  const firstId = idOf(product?.images?.[0]);
                                  const designId = String(
                                    product?.design_id || product?.studio_design_id || ""
                                  ).trim();
                                  const previewId = String(
                                    product?.preview_url || product?.studio_preview_id || ""
                                  ).trim();
                                  const designThumb =
                                    designId && previewId
                                      ? getImageSrc(previewId, imageUrlMap) || buildImageUrl(previewId)
                                      : "";
                                  const src =
                                    designThumb ||
                                    getImageSrc(firstId, imageUrlMap) ||
                                    getImageSrc(product?.image_url, imageUrlMap) ||
                                    buildImageUrl(firstId || product?.image_url);

                                  const editCatalogProductId = String(
                                    product?.productId ?? product?.product_id ?? ""
                                  ).trim();
                                  const placeholderProductId = "000000000000000000000000";
                                  const hasEditableCatalogProductId =
                                    !!editCatalogProductId &&
                                    editCatalogProductId !== placeholderProductId;
                                  const poForCatalogEdit = (ProductObjs || []).find(
                                    (po: any) => String(po?.product_id) === editCatalogProductId
                                  );
                                  const rowProductTypeForEdit = String(
                                    product?.product_type ??
                                      poForCatalogEdit?.product?.product_type ??
                                      ""
                                  ).toLowerCase();
                                  const catalogProductEditHref = hasEditableCatalogProductId
                                    ? rowProductTypeForEdit === "variable"
                                      ? `${baseRoute}/editVariableProduct/${editCatalogProductId}`
                                      : `${baseRoute}/editProduct/${editCatalogProductId}`
                                    : null;

                                  // const key = `${product.productId}:${product.variantId}`;
                                  // const each = effectiveMap[key] ?? Number(product.unitPrice || 0);
                                  // const lineTotal = each * Number(product.quantity || 0);
                                  // const rowKey = `${product.productId}:${product.variantId}`;
                                  // const rowDiscount = Number(perItemCouponMap[rowKey] || 0);

                                  const key = `${product.productId}:${product.variantId}`;
                                  const purchasedErrorKey = purchasedProductErrorKey("catalog", key);
                                  const purchasedRowErrors = purchasedProductFieldErrors[purchasedErrorKey] || {};
                                  const k = keyForRow({ productId: product.productId, variantId: product.variantId });
                                  const qtyNum = Number(product.quantity || 0);
                                  const rowTax = Number(productTaxMap[k] ?? 0);              // tax dollars for this row
                                  const rowTaxPct = Number(productTaxPctMap[k] ?? 0);        // tax rate percent for this row

                                  const catalogBaseUnitPrice = getBaseUnitPrice(
                                    product.productId,
                                    product.variantId,
                                    Number(product.unitPrice || 0)
                                  );
                                  const changedUnitPrice = getUnitPriceToUse(
                                    product.productId,
                                    product.variantId,
                                    Number(product.unitPrice || 0)
                                  );
                                  const unitPriceDisplay =
                                    manualUnitPriceMap[key] !== undefined
                                      ? Number(manualUnitPriceMap[key] || 0)
                                      : catalogBaseUnitPrice;

                                  const { rowDiscount, lineTotal } = getSignageOrderLinePricing({
                                    actualUnitPrice: catalogBaseUnitPrice,
                                    changedUnitPrice,
                                    qty: qtyNum,
                                    tax: rowTax,
                                    isManualUnitPrice: manualUnitPriceMap[key] !== undefined,
                                  });

                                  return (
                                    <tr key={`${product.productId}:${product.variantId}`}>
                                      <td className="table-cells align-middle" style={{ whiteSpace: "normal", wordWrap: "break-word", minWidth: "200px" }}>
                                        <div className="d-flex gap-3 align-items-start">
                                          <div style={{ width: 80, height: 80, flexShrink: 0 }}>
                                            <img
                                              src={src || imagecustomRounded}
                                              alt={product?.ProductName || product?.name || "Product"}
                                              className="img-fluid rounded"
                                                style={{ objectFit: "contain", width: "100%", height: "100%" }}
                                              onError={(e) => {
                                                (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
                                              }}
                                            />
                                          </div>
                                          <div>
                                            <h5 className="fs-15 mb-1 wrap-anywhere">
                                              {catalogProductEditHref ? (
                                                <a
                                                  href={catalogProductEditHref}
                                                  className="link-primary wrap-anywhere"
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                >
                                                  {product.name || product.product_name || product.variant_name || product?.ProductName || "-"}
                                                </a>
                                              ) : (
                                                <span className="wrap-anywhere">
                                                  {product.name || product.product_name || product.variant_name || product?.ProductName || "-"}
                                                </span>
                                              )}
                                            </h5>

                                            {(() => {
                                              const fields = product?.customizations?.fields || [];
                                              if (!fields.length) return null;

                                              const isFile = (f: any) => String(f?.field_type || "").toLowerCase() === "file";
                                              const firstFileVal = (raw: any): string => {
                                                if (!raw) return "";
                                                if (typeof raw === "string") return raw.trim();
                                                if (raw && typeof raw === "object" && raw.url) return String(raw.url).trim();
                                                return "";
                                              };
                                              const hasValue = (f: any): boolean => {
                                                if (isFile(f)) return !!firstFileVal(f?.value);
                                                return String(f?.value ?? "").trim().length > 0;
                                              };
                                              if (!fields.some(hasValue)) return null;

                                              const textFields = fields.filter((f: any) => !isFile(f) && hasValue(f));
                                              const fileFields = fields.filter((f: any) => isFile(f) && hasValue(f));

                                              return (
                                                <div className="custom-fields-container">
                                                  {textFields.map((field: any, idx: number) => (
                                                    <div
                                                      key={`${field?._id || field?.field_name || "cf"}-t-${idx}`}
                                                      className="mb-2"
                                                      style={{ wordBreak: "break-word", maxWidth: 400 }}
                                                    >
                                                      <strong className="custom-cells">{field.field_name} :</strong>{" "}
                                                      <span className="wrap-anywhere">{String(field?.value ?? "").trim()}</span>
                                                    </div>
                                                  ))}
                                                  {fileFields.map((field: any, idx: number) => {
                                                    const id = firstFileVal(field?.value);
                                                    const href = getImageSrc(id, imageUrlMap) || buildImageUrl(id);
                                                    return (
                                                      <div
                                                        key={`${field?._id || field?.field_name || "cf"}-file-${idx}`}
                                                        className="mb-2"
                                                        style={{ wordBreak: "break-word", maxWidth: 400 }}
                                                      >
                                                        <div className="mb-1">{field.field_name}</div>
                                                        <a href={href} download target="_blank" rel="noopener noreferrer">
                                                          <img
                                                            src={href}
                                                            alt={field.field_name}
                                                            className="rounded"
                                                            style={{ maxWidth: 100, height: "auto", cursor: "pointer" }}
                                                          />
                                                        </a>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              );
                                            })()}
                                          </div>
                                        </div>
                                      </td>

                                      {/* SKU */}
                                      <td className="py-2" style={{ minWidth: "80px" }}>
                                        <div className="fw-semibold text-center wrap-anywhere">{formatSkuDisplay(product?.sku)}</div>
                                      </td>

                                      {/* Quantity */}
                                      <td className="py-2 sco-qty-cell">
                                        <input
                                          type="number"
                                          className={`form-control text-center sco-qty-input ${purchasedRowErrors.quantity ? "is-invalid" : ""}`}
                                          min={1}
                                          placeholder="1"
                                          value={(() => {
                                            const qty = product?.quantity ?? 1;
                                            return qty === 0 ? "" : qty;
                                          })()}
                                          onChange={(e) => {
                                            const raw = e.target.value;

                                            // keep UI empty when cleared, but store 0 in state
                                            const qty = raw === "" ? 0 : Number(raw);

                                            handleQuantityChange(index, {
                                              ...e,
                                              target: { ...e.target, value: String(Number.isFinite(qty) ? qty : 0) },
                                            });
                                            patchPurchasedProductFieldError(
                                              purchasedErrorKey,
                                              "quantity",
                                              validatePurchasedQuantityValue(raw)
                                            );
                                          }}
                                          onBlur={(e) => {
                                            patchPurchasedProductFieldError(
                                              purchasedErrorKey,
                                              "quantity",
                                              validatePurchasedQuantityValue(e.target.value)
                                            );
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                                          }}
                                        />
                                        {purchasedRowErrors.quantity ? (
                                          <div className="text-danger small mt-1">{purchasedRowErrors.quantity}</div>
                                        ) : null}
                                      </td>


                                      {/* Unit Price — catalog base_price from assignment-discount API */}
                                      {/* <td className="py-2 text-center sco-unit-price-cell">
                                        <span className="fw-semibold fs-6 sco-unit-price-value">
                                          {catalogBaseUnitPrice.toFixed(2)}
                                        </span>
                                      </td> */}
                                      {/* Unit Price — base/original (manual override when user edits); qty changes update effective price for discount/total only */}
                                      <td className="py-2 sco-changed-price-cell">
                                        <input
                                          type="number"
                                          className={`form-control text-center sco-price-input ${purchasedRowErrors.unitPrice ? "is-invalid" : ""}`}
                                          min={1}
                                          step="0.01"
                                          placeholder="1"
                                          value={(() => {
                                            const price = unitPriceDisplay;
                                            return price === 0 ? "" : price;
                                          })()}
                                          onChange={(e) => {
                                            const raw = e.target.value;
                                            const price = raw === "" ? 0 : Number(raw);
                                            const updated = [...selectedProducts];

                                            const nextPrice = Number.isFinite(price) ? price : 0;
                                            updated[index].unitPrice = nextPrice;
                                            updated[index].unit_retail_price = nextPrice;
                                            updated[index].total = nextPrice * (updated[index].quantity ?? 0);
                                            setSelectedProducts(updated);

                                            setManualUnitPriceMap((prev) => ({
                                              ...prev,
                                              [key]: updated[index].unitPrice ?? 0,
                                            }));

                                            patchPurchasedProductFieldError(
                                              purchasedErrorKey,
                                              "unitPrice",
                                              validatePurchasedUnitPriceValue(raw)
                                            );

                                            setEffectiveMap((prev) => {
                                              const next = { ...prev };
                                              delete next[key];
                                              return next;
                                            });
                                            if (recomputeTimerRef.current) clearTimeout(recomputeTimerRef.current);
                                            recomputeTimerRef.current = setTimeout(() => {
                                              void recomputeSalesRef.current?.();
                                            }, 300);
                                          }}
                                          onBlur={(e) => {
                                            patchPurchasedProductFieldError(
                                              purchasedErrorKey,
                                              "unitPrice",
                                              validatePurchasedUnitPriceValue(e.target.value)
                                            );
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                                          }}
                                        />
                                        {purchasedRowErrors.unitPrice ? (
                                          <div className="text-danger small mt-1">{purchasedRowErrors.unitPrice}</div>
                                        ) : null}
                                      </td>

                                      <td className="text-center align-middle">
                                        <DiscountAmountDisplay amount={rowDiscount} />
                                      </td>

                                      {/* Tax */}
                                      {/* <td className="text-center align-middle" style={{ minWidth: "70px" }}>
                                        {rowTax > 0 ? (
                                          <>
                                            ${rowTax.toFixed(2)}
                                          <small className="text-muted">{rowTaxPct.toFixed(2)}%</small> 
                                          </>
                                        ) : (
                                          "--"
                                        )}
                                      </td> */}

                                      {/* Total = changed unit price × qty + tax */}
                                      <td className="py-2 text-center align-middle sco-total-cell">
                                        <div className="fw-semibold fs-6 sco-total-value">${lineTotal.toFixed(2)}</div>
                                      </td>
                                      <td className="text-center" style={{ minWidth: "50px" }}>
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                          <div className="d-flex align-items-center gap-1">
                                            {isLast && canShowAdd && (
                                              <button
                                                type="button"
                                                className={`border-0 p-0 ${selectedProducts.length === 1 ? " br-8" : ""
                                                  }`}
                                                onClick={() => setPickerOpen(true)}
                                              >
                                                {selectedProducts.length === 1 ? (
                                                  // "Add Product"
                                                  <i className="ri-add-line text-success fs-18" />
                                                ) : (
                                                  <i className="ri-add-line text-success fs-18" />
                                                )}
                                              </button>
                                            )}

                                            {selectedProducts.length > 0 && (
                                              <button
                                                type="button"
                                                className="border-0 p-0"
                                                onClick={() => removeProductRow(index)}
                                              >
                                                <i className="ri-close-line text-danger fs-18" />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                                {customproducts.length > 0 && (
                                  <>
                                    <tr className="table-light">
                                      <td colSpan={8} className="py-2 px-3 fw-semibold" style={{ minWidth: "200px" }}>
                                        <span className="ms-2">Custom Products</span>
                                      </td>
                                    </tr>
                                    {customproducts.map((cp, idx) => {
                                      const customErrorKey = purchasedProductErrorKey("custom", String(idx));
                                      const customRowErrors = purchasedProductFieldErrors[customErrorKey] || {};
                                      const customKey = keyForRow({ productId: cp.product_id, variantId: cp.variant_id });
                                      const rowTax = Number(productTaxMap[customKey] ?? 0);
                                      const qtyNum = Number(cp.quantity || 1);
                                      const unitPrice = Number(cp.Price || 0);
                                      const taxable = cp.is_taxable !== false;
                                      const { lineTotal } = getSignageOrderLinePricing({
                                        actualUnitPrice: unitPrice,
                                        changedUnitPrice: unitPrice,
                                        qty: qtyNum,
                                        tax: taxable ? rowTax : 0,
                                        isManualUnitPrice: true,
                                      });
                                      return (
                                        <tr key={customKey}>
                                          <td className="table-cells align-middle" style={{ whiteSpace: "normal", wordWrap: "break-word", minWidth: "200px" }}>
                                            <div className="d-flex gap-3 align-items-start">
                                              <div style={{ width: 80, height: 80, flexShrink: 0 }} className="bg-light rounded overflow-hidden d-flex align-items-center justify-content-center">
                                                <img
                                                  src={imagecustomRounded}
                                                  alt={cp.item_name || "Custom item"}
                                                  className="img-fluid rounded"
                                                  style={{ objectFit: "contain", width: "100%", height: "100%" }}
                                                  onError={(e) => {
                                                    (e.currentTarget as HTMLImageElement).src = imagecustomRounded;
                                                  }}
                                                />
                                              </div>
                                              <div>
                                                <h5 className="fs-15 mb-1 wrap-anywhere">{cp.item_name || "Custom Item"}</h5>
                                                {cp.custom_information ? (
                                                  <div className="small wrap-anywhere" style={{ maxWidth: 400 }}>{cp.custom_information}</div>
                                                ) : null}
                                              </div>
                                            </div>
                                          </td>
                                          <td className="py-2 text-center">{formatSkuDisplay(cp?.sku)}</td>
                                          <td className="py-2" style={{ minWidth: "80px" }}>
                                            <input
                                              type="number"
                                              className={`form-control text-center ${customRowErrors.quantity ? "is-invalid" : ""}`}
                                              min={1}
                                              placeholder="1"
                                              value={qtyNum === 0 ? "" : qtyNum}
                                              onChange={(e) => {
                                                const raw = e.target.value;
                                                const qty = raw === "" ? 0 : Number(raw);
                                                updateCustomProduct(idx, { quantity: Number.isFinite(qty) ? qty : 0 });
                                                patchPurchasedProductFieldError(
                                                  customErrorKey,
                                                  "quantity",
                                                  validatePurchasedQuantityValue(raw)
                                                );
                                              }}
                                              onBlur={(e) => {
                                                patchPurchasedProductFieldError(
                                                  customErrorKey,
                                                  "quantity",
                                                  validatePurchasedQuantityValue(e.target.value)
                                                );
                                              }}
                                              onKeyDown={(e) => {
                                                if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                                              }}
                                            />
                                            {customRowErrors.quantity ? (
                                              <div className="text-danger small mt-1">{customRowErrors.quantity}</div>
                                            ) : null}
                                          </td>
                                          <td className="py-2" style={{ minWidth: "90px" }}>
                                            <input
                                              type="number"
                                              className={`form-control text-center ${customRowErrors.unitPrice ? "is-invalid" : ""}`}
                                              min={1}
                                              step="0.01"
                                              placeholder="1"
                                              value={unitPrice === 0 ? "" : unitPrice}
                                              onChange={(e) => {
                                                const raw = e.target.value;
                                                const price = raw === "" ? 0 : Number(raw);
                                                updateCustomProduct(idx, { Price: Number.isFinite(price) ? price : 0 });
                                                patchPurchasedProductFieldError(
                                                  customErrorKey,
                                                  "unitPrice",
                                                  validatePurchasedUnitPriceValue(raw)
                                                );
                                              }}
                                              onBlur={(e) => {
                                                patchPurchasedProductFieldError(
                                                  customErrorKey,
                                                  "unitPrice",
                                                  validatePurchasedUnitPriceValue(e.target.value)
                                                );
                                              }}
                                              onKeyDown={(e) => {
                                                if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                                              }}
                                            />
                                            {customRowErrors.unitPrice ? (
                                              <div className="text-danger small mt-1">{customRowErrors.unitPrice}</div>
                                            ) : null}
                                          </td>
                                          <td className="text-center align-middle">-</td>
                                          {/* <td className="text-center align-middle" style={{ minWidth: "70px" }}>
                                            {taxable && rowTax > 0 ? (
                                              <>${rowTax.toFixed(2)}</>
                                            ) : (
                                              "--"
                                            )}
                                          </td> */}
                                          <td className="py-2 text-center align-middle sco-total-cell">
                                            <div className="fw-semibold fs-6 sco-total-value">${lineTotal.toFixed(2)}</div>
                                          </td>
                                          <td className="text-center" style={{ minWidth: "50px" }}>
                                            <button
                                              type="button"
                                              className="border-0 p-0"
                                              onClick={() => removeCustomProduct(idx)}
                                            >
                                              <i className="ri-close-line text-danger fs-18" />
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </>
                                )}
                              </>
                            )}
                          </tbody>
                        </table>
                      </div>
                      </>
                    </div>
                    </>
                    {String(linkedProjectId || "").trim() && hasApprovedMessage ? (
                      <div className="row col-lg-12 mb-3 ms-0 pt-4">
                        {approvedProofCards.map((card) => (
                          <CompactApprovedProofCard
                            key={`approved-proof-v-${card.version}`}
                            version={card.version}
                            files={card.files}
                            approvedBy={card.approvedByName}
                            approvedAt={card.approvedAtLabel}
                            footerBy={card.assignedToName}
                            shippingName={card.shippingName}
                            shippingDesc={card.shippingDesc}
                            shippingAmountLabel={card.shippingAmountLabel}
                            showOrderSummary={isSignagePortalRoute(location.pathname, baseRoute)}
                            orderSummary={card.orderSummary}
                            onDownloadByFileId={(fileId, fileName) => {
                              void downloadWorkDriveFile(fileId, fileName);
                            }}
                          />
                        ))}
                        </div>
                    ) : null}
                    <SignageCreateOrderInstructionsAndTotals
                      notes={formik.values.notes}
                      onNotesChange={(v) => formik.setFieldValue("notes", v, false)}
                      orderFormResetKey={orderFormResetKey}
                      token={token || ""}
                      supplierId={(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}
                      couponInput={couponInput}
                      appliedCouponCode={liveDiscountDisplay.code}
                      appliedCouponAmount={liveDiscountDisplay.amount}
                      voucherApplied={
                        liveDiscountDisplay.amount > 0 &&
                        Boolean(liveDiscountDisplay.code.trim())
                      }
                      couponResolveDisabled={isRecomputingSales}
                      couponError={couponResolveError}
                      onCouponInputChange={setCouponInput}
                      onCouponApply={(code) => {
                        const trimmed = code.trim();
                        if (!trimmed) return;
                        userClearedCouponRef.current = false;
                        setCouponInput(trimmed);
                        setLiveDiscountDisplay({ code: "", amount: 0 });
                        setPerItemCouponMap({});
                        void recomputeSalesRef.current?.({ force: true, discountCode: trimmed });
                      }}
                      onCouponClear={() => {
                        setCouponInput("");
                        setCouponResolveError("");
                        userClearedCouponRef.current = true;
                        setLiveDiscountDisplay({ code: "", amount: 0 });
                        setPerItemCouponMap({});
                        void recomputeSalesRef.current?.({ force: true, discountCode: "" });
                      }}
                      formatMoney={money}
                      subTotalAllBeforeDiscount={subTotalAllBeforeDiscount}
                      canEditShipping={canEditShipping}
                      isEditingShipping={isEditingShipping}
                      manualShippingAmount={manualShippingAmount}
                      onManualShippingAmountChange={setManualShippingAmount}
                      onSaveShipping={handleSaveShipping}
                      onCancelEditShipping={handleCancelEditShipping}
                      onEditShipping={handleEditShipping}
                      shippingAmount={shippingAmount}
                      specialFeeAmount={specialFeeAmount}
                      showTaxDetails={showTaxDetails}
                      onToggleTaxDetails={() => setShowTaxDetails((v) => !v)}
                      totalTaxAll={totalTaxAll}
                      grandTotal={grandTotal}
                      onPendingInstructionFilesChange={handlePendingInstructionFilesChange}
                      shippingOptionRows={shippingRowsForTotalsLabel}
                      isUpsShipping={isUpsShippingForTotalsLabel}
                      specialFeeRows={specialFeeRowsForTotalsLabel}
                    />
                    <div className="col-12 mb-2" id="row-overlap">
                      <hr className="ms-4 me-3" />
                    </div>
                    <div className="row mb-4 rcust-margin-4">
                      <div className="align-items-end col-md-12 d-flex justify-content-between">
                        <button
                          className="btn back-btn surface-btn fw-bold fs-16"
                          type="button"
                          onClick={() => safeNavigate(-1)}
                        >
                          <i className="bi bi-chevron-left me-2" />
                          Back
                        </button>
                        {formError && <div className="text-danger mb-0">{formError}</div>}
                        <div className=" text-end">
                          <button
                            className="btn prim-btnary save-btn surface-btn fw-bold fs-16"
                            type="submit"
                            data-save-button="true"
                            disabled={isAnythingBusy || !canProceedToSave}
                            onClick={handleSaveDraft}
                            title={
                              isAnythingBusy && !formSubmitting
                                ? "Processing..."
                                : !canProceedToSave
                                  ? "Please select shipping address and shipping option"
                                  : ""
                            }
                          >
                            {formSubmitting ? "Submitting..." : isAnythingBusy ? "Processing..." : "Save"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                { }
                {pickerOpen && (
                  <ProductPickerModal
                    open={true}
                    onClose={closePicker}
                    supplierId={(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}
                    createdById={effectiveCouponCreatedById || ""}
                    onAdd={handleAddFromPicker}
                    existingLineKeys={pickerExistingLineKeys}
                    studioBatchVariantPreviews={
                      Object.keys(mergedStudioBatchVariantPreviewsForPicker).length
                        ? mergedStudioBatchVariantPreviewsForPicker
                        : undefined
                    }
                    studioCustomization={createOrderStudioCustomization}
                  />
                )}

                {showAddressForm && (
                  <AddressFormModal
                    isOpen={true}
                    onClose={() => {
                      setShowAddressForm(false);
                    }}
                    token={token || ""}
                    owner={selectedOfficeAgent || selectedAgent || selectedOffice}
                    useType="billing"
                    userType={
                      formik.values.userType === "brokerageFirm"
                        ? selectedOfficeAgent
                          ? "agent"
                          : selectedTeamMemberOption || selectedOffice
                            ? "office"
                            : undefined
                        : formik.values.userType === "agent"
                          ? "agent"
                          : selectedOfficeAgent || selectedAgent
                            ? "agent"
                            : selectedOffice
                              ? "office"
                              : undefined
                    }

                    prefill={{
                      // Prefill from your existing parent formik values (same field names you used)
                      label_of_address: formik?.values?.label_of_address || "",
                      firstName: formik?.values?.firstName || "",
                      lastName: formik?.values?.lastName || "",
                      company: formik?.values?.company || "",
                      phone: formik?.values?.phone || "",
                      addressLine1: formik?.values?.addressLine1 || "",
                      addressLine2: formik?.values?.addressLine2 || "",
                      city: formik?.values?.city || "",
                      state: formik?.values?.state || "",
                      zipcode: formik?.values?.zipcode || "",
                      country: formik?.values?.country || "",
                    }}
                    onCreated={(createdRaw, option, useType) => {
                      // setAddressObjects((prev) => [createdRaw, ...(prev || [])]);
                      setAddressObjects((prev) => [createdRaw, ...(prev || [])]);  // correct

                      formik.setFieldValue(
                        useType === "billing" ? "billingAddressId" : "shippingAddressId",
                        option.value
                      );
                      const isOffice = !!selectedOffice && !selectedOfficeAgent && !selectedAgent;

                      if (useType === "billing") {
                        if (isOffice) {
                          setSelectedOfficeBillingAddress(createdRaw);
                        } else {
                          setSelectedBillingAddress(createdRaw?.address_details || null);
                        }
                      } else {
                        if (isOffice) {
                          setSelectedOfficeShippingAddress(createdRaw);
                        } else {
                          setSelectedShippingAddress(createdRaw?.address_details || null);
                        }
                      }

                      // 4) Mirror to shipping if same-as-billing is checked
                      if (useType === "billing" && formik.values.isSameAsBilling) {
                        formik.setFieldValue("shippingAddressId", option.value);
                        if (isOffice) {
                          setSelectedOfficeShippingAddress(createdRaw);
                        } else {
                          setSelectedShippingAddress(createdRaw?.address_details || null);
                        }
                      }
                    }}
                    afterCreateRefresh={async () => {
                      if (selectedOfficeAgent) await fetchAddresses(selectedOfficeAgent);
                      else if (selectedAgent) await fetchAddresses(selectedAgent);
                      else if (selectedOffice) await fetchAddresses(selectedOffice);
                    }}
                  />
                )}
              </form>
              {formSubmitting && (
                <div className="mt-3">
                  <div className="progress">
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${uploadProgress}%` }}
                      aria-valuenow={uploadProgress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      {uploadProgress}%
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <PONumber
            mode="create"
            disabled={Boolean(String(linkedProjectId || projectIdFromQuery || "").trim())}
            className="mb-3"
            value={String(formik.values.po_number || "")}
            onChange={(v) => formik.setFieldValue("po_number", v)}
          />
          {
            !order && baseId &&
            <ShippingOptions
              key={`ship-${orderFormResetKey}-${
                studioRestoreShippingInitialIds
                  ? `${studioRestoreShippingInitialIds.manual_shipping_id ?? ""}-${studioRestoreShippingInitialIds.ups_service_code ?? ""}`
                  : "no-studio-ship"
              }`}
              ref={shipRef}
              token={token!}
              shippingAddress={shippingAddressForRates}
              totalQty={totalQtyForShipping}
              selectedProducts={selectedProductsWithDims}
              productsFull={Array.isArray(ProductObjs) ? ProductObjs.map(p => ({ product_id: p.product_id, product: p.product })) : []}
              createdById={(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}
              supplierId={(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}
              initialSelectedIds={studioRestoreShippingInitialIds}
              savedShippingRows={studioRestoreShippingSavedRows ?? undefined}
              onPayloadChange={handleShipPayloadChange}
              onBusyChange={handleShippingBusyChange}
            // ✅ emit → parent state (with dedupe)
            // onPayloadChange={React.useCallback((p: any) => {
            //   setShipPayloadState(prev => {
            //     const same = JSON.stringify({
            //       rows: (prev?.shipping_option || []).map((r: any) => [r.shipping__id, r.shipping_amount, r.shipping_tax_amount]),
            //       ids: prev?.selected_ids
            //     }) === JSON.stringify({
            //       rows: (p?.shipping_option || []).map((r: any) => [r.shipping__id, r.shipping_amount, r.shipping_tax_amount]),
            //       ids: p?.selected_ids
            //     });
            //     return same ? prev : (p as any);
            //   });
            // }, [])}
            />
          }
          <SpecialFeePicker
            key={`fee-${effectiveCouponCreatedById || "no-person"}-${studioRestoreSpecialFeeInitialIds ? studioRestoreSpecialFeeInitialIds.join("-") : "no-studio-fee"}`}
            ref={feeRef}
            token={token!}
            personId={effectiveCouponCreatedById || ""}                    // createdBy / user id
            supplierId={(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}
            shippingAddress={shippingAddressForRates}
            knownTaxType="manual"
            isTaxable
            statusOk
            initialSelectedIds={studioRestoreSpecialFeeInitialIds}
            onPayloadChange={handleFeePayloadChange}
            onBusyChange={handleSpecialFeeBusyChange}
            onSourceFeesLoaded={(fees) => {
              specialFeeCatalogRef.current = fees;
            }}
          // onPayloadChange={React.useCallback((p: FeePayload) => {
          //   setFeePayloadState(prev => {
          //     const prevKey = JSON.stringify({
          //       rows: (prev?.specialfee ?? []).map(f => [
          //         f.specialfee_id,
          //         f.specialfee_amount,
          //         f.specialfee_tax_amount,
          //       ]),
          //       ids: prev?.selected_fee_ids,
          //     });
          //     const nextKey = JSON.stringify({
          //       rows: (p?.specialfee ?? []).map(f => [
          //         f.specialfee_id,
          //         f.specialfee_amount,
          //         f.specialfee_tax_amount,
          //       ]),
          //       ids: p?.selected_fee_ids,
          //     });
          //     return prevKey === nextKey ? prev : p;
          //   });
          // }, [])}
          />
          <ProjectGetCard
            token={token!}
            signageId={(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}
            initialProjectId={
              userClearedProjectRef.current
                ? undefined
                : linkedProjectId || projectIdFromQuery || undefined
            }
            onSelectionChange={(sel) => {
              if (!sel) userClearedProjectRef.current = true;
              else userClearedProjectRef.current = false;
              setLinkedProjectId(sel?.projectId ?? null);
              setLinkedProjectNumber(sel?.projectNumber ?? null);
            }}
          />

        </div>
      </div>
      <style>{`
.form-control.text-center::placeholder {
  text-align: center;
}
.form-control.text-center::-webkit-input-placeholder {
  text-align: center;
}
.form-control.text-center:-ms-input-placeholder {
  text-align: center;
}

.signage-create-order-products-table {
  table-layout: fixed;
  min-width: 900px;
  width: 100%;
}

.signage-create-order-products-table .sco-col-product { width: 28%; }
.signage-create-order-products-table .sco-col-sku { width: 10%; }
.signage-create-order-products-table .sco-col-qty { width: 7%; }
.signage-create-order-products-table .sco-col-unit-price { width: 11%; }
.signage-create-order-products-table .sco-col-changed-price { width: 12%; }
.signage-create-order-products-table .sco-col-discount { width: 9%; }
.signage-create-order-products-table .sco-col-tax { width: 8%; }
.signage-create-order-products-table .sco-col-total { width: 11%; }
.signage-create-order-products-table .sco-col-actions { width: 4%; }

.signage-create-order-products-table th.table-headers {
  font-size: 0.78rem;
  line-height: 1.2;
  white-space: normal;
  vertical-align: middle;
  padding: 0.5rem 0.35rem;
}

.signage-create-order-products-table .sco-unit-price-cell,
.signage-create-order-products-table .sco-changed-price-cell,
.signage-create-order-products-table .sco-qty-cell,
.signage-create-order-products-table .sco-total-cell {
  padding-left: 0.35rem;
  padding-right: 0.35rem;
}

.signage-create-order-products-table .sco-unit-price-value,
.signage-create-order-products-table .sco-total-value {
  display: inline-block;
  white-space: nowrap;
}

.signage-create-order-products-table .sco-price-input,
.signage-create-order-products-table .sco-qty-input {
  display: block;
  width: 100%;
  min-width: 5.5rem;
  max-width: 7.5rem;
  margin: 0 auto;
  padding: 0.35rem 0.5rem;
  font-size: 0.875rem;
  box-sizing: border-box;
}

.signage-create-order-products-table .sco-qty-input {
  min-width: 3.25rem;
  max-width: 4.5rem;
}

@media (max-width: 767.98px) {
  .signage-create-order-products-table {
    min-width: 980px;
  }

  .signage-create-order-products-table th.table-headers {
    font-size: 0.72rem;
    padding: 0.45rem 0.25rem;
  }

  .signage-create-order-products-table .sco-price-input {
    min-width: 6.5rem;
    max-width: 8rem;
    padding: 0.4rem 0.55rem;
  }
}
      `}</style>
    </div>
  );
};

export default SignageCreateOrder;


